import Phaser from "phaser";
import type { EnemyId } from "../types";
import { ENEMIES } from "../data/enemies";

/**
 * Procedurally drawn creep sprite — built from Phaser primitives so we don't
 * need an asset pipeline. Each enemy type gets a unique silhouette + accent.
 *
 * Returns a Container at (x,y). The container has a `radius` field for hit
 * tests and a `setTookHit()` helper for hit flash.
 */
export interface CreepSprite extends Phaser.GameObjects.Container {
  radius: number;
  setTookHit: () => void;
}

export function makeCreep(scene: Phaser.Scene, id: EnemyId, x: number, y: number): CreepSprite {
  const c = scene.add.container(x, y) as CreepSprite;

  // ---- PREMIUM CREEP UNDER-GLOW (added behind body) ----
  // A subtle, color-tinted ground glow that grounds the creep in the path
  // and reads its threat level at a glance. Drawn first so the creep body
  // sits on top; size scales with displayed radius.
  const meta = ENEMIES[id];
  const glowSize = Math.max(14, meta.radius * 0.85);
  const isMega = id === "overlord" || id === "warlord" || id === "worldender";
  const groundGlow = scene.add.ellipse(0, isMega ? 28 : 16, glowSize * 1.6, glowSize * 0.42, meta.color, 0.28);
  const groundCore = scene.add.ellipse(0, isMega ? 28 : 16, glowSize * 0.95, glowSize * 0.28, 0xffffff, 0.18);
  scene.tweens.add({
    targets: [groundGlow, groundCore],
    scaleX: { from: 0.85, to: 1.18 },
    alpha: { from: 0.18, to: 0.38 },
    yoyo: true, repeat: -1, duration: 900 + Math.random() * 300,
    ease: "Sine.easeInOut",
  });
  c.add([groundGlow, groundCore]);

  switch (id) {
    case "grunt":
      buildGrunt(scene, c);
      c.radius = 20;
      c.setScale(1.85);
      break;
    case "fast":
      buildFast(scene, c);
      c.radius = 17;
      c.setScale(1.8);
      break;
    case "tank":
      buildTank(scene, c);
      c.radius = 25;
      c.setScale(1.8);
      break;
    case "boss":
      buildBoss(scene, c);
      c.radius = 32;
      c.setScale(1.8);
      break;
    case "elite":
      buildElite(scene, c);
      c.radius = 22;
      c.setScale(1.8);
      break;
    case "titan":
      buildTitan(scene, c);
      c.radius = 36;
      c.setScale(1.8);
      break;
    case "wraith":
      buildWraith(scene, c);
      c.radius = 22;
      c.setScale(1.8);
      break;
    case "hellhound":
      buildHellhound(scene, c);
      c.radius = 20;
      c.setScale(1.75);
      break;
    case "golem":
      buildGolem(scene, c);
      c.radius = 30;
      c.setScale(1.8);
      break;
    case "brood":
      buildBrood(scene, c);
      c.radius = 24;
      c.setScale(1.8);
      break;
    // Mega-bosses — built using existing boss/titan silhouettes inside a
    // sub-container scaled 3x so the outer bob-tween can keep using `from: 1`.
    case "overlord": {
      const inner = scene.add.container(0, 0);
      buildBoss(scene, inner);
      const sc = 2.0;
      inner.setScale(sc);
      // Lift inner so feet (boss shadow at local y≈22) sit just below the path,
      // matching the visual offset of normal creeps instead of floating above.
      inner.y = -22 * (sc - 1);
      const auraRing = scene.add.circle(0, inner.y, 80, 0xff4a4a, 0.22);
      const auraInner = scene.add.circle(0, inner.y, 50, 0xff4a4a, 0.32);
      scene.tweens.add({ targets: [auraRing, auraInner], scale: { from: 0.85, to: 1.15 }, alpha: { from: 0.15, to: 0.4 }, yoyo: true, repeat: -1, duration: 800 });
      c.add([auraRing, auraInner, inner]);
      c.radius = 50;
      break;
    }
    case "warlord": {
      const inner = scene.add.container(0, 0);
      buildTitan(scene, inner);
      const sc = 2.0;
      inner.setScale(sc);
      inner.y = -22 * (sc - 1);
      const auraRing = scene.add.circle(0, inner.y, 95, 0xb84aff, 0.22);
      const auraInner = scene.add.circle(0, inner.y, 60, 0xb84aff, 0.36);
      scene.tweens.add({ targets: [auraRing, auraInner], scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.18, to: 0.45 }, yoyo: true, repeat: -1, duration: 900 });
      const runeRing = scene.add.circle(0, inner.y, 70, 0xffffff, 0).setStrokeStyle(2, 0xd4a0ff, 0.85);
      scene.tweens.add({ targets: runeRing, scale: { from: 0.95, to: 1.1 }, alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 700 });
      c.add([auraRing, auraInner, runeRing, inner]);
      c.radius = 56;
      break;
    }
    case "skeleton":
      buildSkeleton(scene, c);
      c.radius = 15;
      c.setScale(1.75);
      break;
    case "demon":
      buildDemon(scene, c);
      c.radius = 22;
      c.setScale(1.8);
      break;
    case "spider":
      buildSpider(scene, c);
      c.radius = 16;
      c.setScale(1.75);
      break;
    case "crystal":
      buildCrystal(scene, c);
      c.radius = 26;
      c.setScale(1.8);
      break;
    case "vampire":
      buildVampire(scene, c);
      c.radius = 19;
      c.setScale(1.8);
      break;
    case "magma_knight":
      buildMagmaKnight(scene, c);
      c.radius = 28;
      c.setScale(1.8);
      break;
    case "shadow":
      buildShadow(scene, c);
      c.radius = 14;
      c.setScale(1.75);
      break;
    case "worldender": {
      const inner = scene.add.container(0, 0);
      buildBoss(scene, inner);
      const sc = 2.3;
      inner.setScale(sc);
      inner.y = -22 * (sc - 1);
      const auraOuter = scene.add.circle(0, inner.y, 115, 0x4a0a4a, 0.3);
      const auraMid = scene.add.circle(0, inner.y, 85, 0xc8d040, 0.32);
      const auraCore = scene.add.circle(0, inner.y, 58, 0xff4adc, 0.4);
      scene.tweens.add({ targets: [auraOuter, auraMid, auraCore], scale: { from: 0.85, to: 1.25 }, alpha: { from: 0.2, to: 0.55 }, yoyo: true, repeat: -1, duration: 1000 });
      const ringA = scene.add.circle(0, inner.y, 72, 0xffffff, 0).setStrokeStyle(2.5, 0xc8d040, 0.9);
      const ringB = scene.add.circle(0, inner.y, 92, 0xffffff, 0).setStrokeStyle(2, 0xff4adc, 0.85);
      scene.tweens.add({ targets: ringA, scale: { from: 0.95, to: 1.12 }, yoyo: true, repeat: -1, duration: 800 });
      scene.tweens.add({ targets: ringB, scale: { from: 1.1, to: 0.92 }, yoyo: true, repeat: -1, duration: 950 });
      for (let i = 0; i < 6; i++) {
        const ang = (i / 6) * Math.PI * 2;
        const skull = scene.add.circle(Math.cos(ang) * 65, Math.sin(ang) * 65 + inner.y, 5, 0xeae0c0, 0.85);
        c.add(skull);
        scene.tweens.add({ targets: skull, alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 600 + i * 80, delay: i * 100 });
      }
      c.add([auraOuter, auraMid, auraCore, ringA, ringB, inner]);
      c.radius = 62;
      break;
    }
  }

  // Bounce animation — different speed per type for personality.
  const bobScale =
    id === "fast" ? 0.18 :
    id === "tank" ? 0.06 :
    id === "boss" ? 0.05 :
    id === "titan" ? 0.04 :
    id === "wraith" ? 0.10 :
    id === "hellhound" ? 0.16 :
    id === "golem" ? 0.04 :
    id === "brood" ? 0.14 :
    id === "overlord" ? 0.04 :
    id === "warlord" ? 0.035 :
    id === "worldender" ? 0.03 :
    id === "skeleton" ? 0.14 :
    id === "demon" ? 0.08 :
    id === "spider" ? 0.2 :
    id === "crystal" ? 0.03 :
    id === "vampire" ? 0.11 :
    id === "magma_knight" ? 0.05 :
    id === "shadow" ? 0.22 :
    0.12;
  const bobMs =
    id === "fast" ? 180 :
    id === "tank" ? 480 :
    id === "boss" ? 650 :
    id === "titan" ? 600 :
    id === "wraith" ? 520 :
    id === "hellhound" ? 200 :
    id === "golem" ? 540 :
    id === "brood" ? 280 :
    id === "overlord" ? 750 :
    id === "warlord" ? 850 :
    id === "worldender" ? 950 :
    id === "skeleton" ? 240 :
    id === "demon" ? 420 :
    id === "spider" ? 160 :
    id === "crystal" ? 600 :
    id === "vampire" ? 320 :
    id === "magma_knight" ? 520 :
    id === "shadow" ? 150 :
    260;
  scene.tweens.add({
    targets: c,
    scaleY: { from: 1, to: 1 - bobScale },
    scaleX: { from: 1, to: 1 + bobScale * 0.4 },
    yoyo: true,
    repeat: -1,
    duration: bobMs,
    ease: "Sine.easeInOut",
  });

  // Hit reaction — quick white flash.
  c.setTookHit = () => {
    scene.tweens.killTweensOf({ flashIntensity: 0 });
    const flash = scene.add.circle(0, 0, c.radius + 1, 0xffffff, 0.18);
    c.add(flash);
    scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 110,
      onComplete: () => flash.destroy(),
    });
  };

  return c;
}

// ---------- per-type builders ----------

function buildGrunt(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // GOBLIN GRUNT — classic wave-1 trash mob: green skin, ragged loincloth,
  // crude wooden club, jagged tusks, pointed ears, beady yellow eyes.
  // Reads as a low-tier humanoid raider.
  const SKIN_DK = 0x2a4012;   // shadowed green
  const SKIN    = 0x6fae3a;   // goblin green body
  const SKIN_HI = 0x9ad054;   // sun-lit highlight
  const CLOTH   = 0x6a3018;   // mud-brown loincloth
  const CLOTH_HI= 0x8a4828;   // stitched seam
  const BELT    = 0x382010;   // dark leather belt
  const WOOD    = 0x5a3818;   // wooden club shaft
  const WOOD_HI = 0x8a5828;   // wood grain hi
  const TUSK    = 0xeae4c0;   // ivory tusk
  const EYE     = 0xffd848;   // yellow beady eye
  const NAIL    = 0x301810;   // dirty fingernail
  const NOSE    = 0x4a7020;   // shadowed nose

  const shadow = scene.add.ellipse(0, 14, 20, 4.5, 0x000000, 0.42);

  // ---- LEGS (short, bandy) ----
  const legL = scene.add.rectangle(-3.2, 9, 3.6, 8, SKIN).setStrokeStyle(0.6, SKIN_DK, 1);
  const legR = scene.add.rectangle(3.2, 9, 3.6, 8, SKIN).setStrokeStyle(0.6, SKIN_DK, 1);
  const footL = scene.add.ellipse(-3.5, 13, 4.5, 2.2, SKIN_DK);
  const footR = scene.add.ellipse(3.5, 13, 4.5, 2.2, SKIN_DK);
  const toeL1 = scene.add.triangle(-5, 13, 0, 0, -0.7, 1.2, 0.7, 1.2, NAIL);
  const toeR1 = scene.add.triangle(5, 13, 0, 0, -0.7, 1.2, 0.7, 1.2, NAIL);

  // ---- LOINCLOTH (ragged) ----
  const clothBack = scene.add.polygon(0, 6, [-6, 0, -7, 6, -3, 4, 0, 7, 3, 4, 7, 6, 6, 0], CLOTH, 0.95).setStrokeStyle(0.5, 0x1a0a04, 1);
  const clothSeam = scene.add.line(0, 0, -5, 4, 5, 4, CLOTH_HI, 0.7).setLineWidth(0.5).setOrigin(0, 0);
  const belt = scene.add.rectangle(0, 2, 13, 1.6, BELT).setStrokeStyle(0.4, 0x1a0a04, 1);
  const beltBuckle = scene.add.rectangle(0, 2, 2.2, 2, 0xbfa050).setStrokeStyle(0.3, BELT, 1);

  // ---- BODY (hunched, lean) ----
  const body = scene.add.ellipse(0, 0, 15, 12, SKIN).setStrokeStyle(0.9, SKIN_DK, 1);
  const bodyHi = scene.add.ellipse(-2, -2, 6, 5, SKIN_HI, 0.4);
  const ribShadow = scene.add.line(0, 0, -4, -1, 4, -1, SKIN_DK, 0.5).setLineWidth(0.5).setOrigin(0, 0);

  // ---- ARMS — one raised with club, one swinging ----
  // Right arm raised — wielding club
  const armR = scene.add.rectangle(7, -3, 2.4, 9, SKIN).setStrokeStyle(0.5, SKIN_DK, 1).setRotation(0.7);
  const handR = scene.add.circle(11, 1, 1.6, SKIN).setStrokeStyle(0.4, SKIN_DK, 1);
  // Crude wooden club above
  const club = scene.add.rectangle(13, -4, 2.6, 11, WOOD).setStrokeStyle(0.5, 0x1a0a04, 1).setRotation(0.4);
  const clubHead = scene.add.ellipse(15, -8, 5, 5, WOOD).setStrokeStyle(0.6, 0x1a0a04, 1);
  const clubKnot1 = scene.add.circle(15, -7, 0.8, WOOD_HI);
  const clubKnot2 = scene.add.circle(14, -9, 0.7, WOOD_HI);
  const clubSpike = scene.add.triangle(17, -10, 0, 0, -1, 2, 1, 2, TUSK).setRotation(0.6);
  // Left arm swinging
  const armL = scene.add.rectangle(-7, 2, 2.4, 9, SKIN).setStrokeStyle(0.5, SKIN_DK, 1).setRotation(-0.45);
  const handL = scene.add.circle(-10, 6, 1.6, SKIN).setStrokeStyle(0.4, SKIN_DK, 1);
  const fNailL1 = scene.add.triangle(-11, 7, 0, 0, -0.5, 1.4, 0.5, 1.4, NAIL).setRotation(-0.5);
  const fNailL2 = scene.add.triangle(-10, 7.5, 0, 0, -0.5, 1.4, 0.5, 1.4, NAIL);
  scene.tweens.add({ targets: [armR, club, clubHead, clubKnot1, clubKnot2, clubSpike, handR], y: "-=2", yoyo: true, repeat: -1, duration: 380, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [armL, handL, fNailL1, fNailL2], y: "+=1.5", yoyo: true, repeat: -1, duration: 360, ease: "Sine.easeInOut" });

  // ---- HEAD (oversized, pointed ears, long crooked nose) ----
  const head = scene.add.ellipse(0, -9, 13, 12, SKIN).setStrokeStyle(0.9, SKIN_DK, 1);
  const headHi = scene.add.ellipse(-2.5, -11, 5, 3.5, SKIN_HI, 0.45);
  // Pointed ears
  const earL = scene.add.triangle(-7.5, -10, 0, 0, -3.5, -3, 0, 3, SKIN).setStrokeStyle(0.5, SKIN_DK, 1);
  const earR = scene.add.triangle(7.5, -10, 0, 0, 3.5, -3, 0, 3, SKIN).setStrokeStyle(0.5, SKIN_DK, 1);
  const earInL = scene.add.triangle(-6.5, -10, 0, 0, -1.4, -1.2, 0, 1, SKIN_DK);
  const earInR = scene.add.triangle(6.5, -10, 0, 0, 1.4, -1.2, 0, 1, SKIN_DK);
  // Long crooked nose
  const nose = scene.add.triangle(0, -7, 0, 0, -1.4, 3.5, 1.4, 3.5, NOSE).setStrokeStyle(0.4, SKIN_DK, 1);
  const noseHi = scene.add.circle(0, -5.5, 0.5, SKIN_HI, 0.6);

  // Beady yellow eyes
  const eyeSockL = scene.add.ellipse(-3.4, -11, 3, 2.2, 0x1a0808);
  const eyeSockR = scene.add.ellipse(3.4, -11, 3, 2.2, 0x1a0808);
  const eyeL = scene.add.circle(-3.4, -11, 1.1, EYE);
  const eyeR = scene.add.circle(3.4, -11, 1.1, EYE);
  const pupL = scene.add.circle(-3.0, -11, 0.45, 0x000);
  const pupR = scene.add.circle(3.8, -11, 0.45, 0x000);
  const browL = scene.add.rectangle(-3.4, -13, 4, 0.9, SKIN_DK).setRotation(-0.25);
  const browR = scene.add.rectangle(3.4, -13, 4, 0.9, SKIN_DK).setRotation(0.25);
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: { from: 0.85, to: 1 }, yoyo: true, repeat: -1, duration: 620 });

  // Mouth — grinning crooked, with tusks poking up
  const mouth = scene.add.rectangle(0, -4.5, 7, 1.8, 0x1a0808).setRotation(-0.05);
  const tuskL = scene.add.triangle(-1.5, -5, 0, 0, -0.7, -2.4, 0.7, -2.4, TUSK);
  const tuskR = scene.add.triangle(1.5, -5, 0, 0, -0.7, -2.4, 0.7, -2.4, TUSK);
  const tooth1 = scene.add.rectangle(-0.8, -4.2, 0.5, 1.1, TUSK);
  const tooth2 = scene.add.rectangle(0.8, -4.2, 0.5, 1.1, TUSK);

  c.add([
    shadow,
    legL, legR, footL, footR, toeL1, toeR1,
    clothBack, clothSeam, belt, beltBuckle,
    body, bodyHi, ribShadow,
    armL, handL, fNailL1, fNailL2,
    head, headHi,
    earL, earR, earInL, earInR,
    nose, noseHi,
    eyeSockL, eyeSockR, eyeL, eyeR, pupL, pupR, browL, browR,
    mouth, tuskL, tuskR, tooth1, tooth2,
    armR, handR,
    club, clubHead, clubKnot1, clubKnot2, clubSpike,
  ]);
}

function buildFast(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // GIANT TARANTULA — extremely hairy, 8 glowing red eyes, dripping venom fangs,
  // blood-red abdomen markings, dense radial bristles, twitching legs.

  const HAIR     = 0x040000;   // darkest hair
  const HAIR_HI  = 0x3a1818;   // rusty bristle highlight
  const BODY_DK  = 0x080406;   // near-black body
  const BODY_MID = 0x180810;   // dark purple-black
  const BODY_HI  = 0x2a1218;   // brownish-red highlight
  const EYE_RED  = 0xff2010;   // glowing eye red
  const VENOM    = 0x99dd22;   // venom green-yellow
  const FANG     = 0xece4c8;   // bone fang color
  const MARK     = 0x880810;   // blood marking

  const shadow = scene.add.ellipse(0, 9, 32, 8, 0x000000, 0.6);
  const silk = scene.add.line(0, 0, -18, 2, -8, 1, 0xeeeeee, 0.5).setLineWidth(0.7).setOrigin(0, 0);
  scene.tweens.add({ targets: silk, alpha: 0.15, yoyo: true, repeat: -1, duration: 220 });

  // ---- 8 LEGS — jointed, with bristly hairs along each segment ----
  const legs: Phaser.GameObjects.GameObject[] = [];
  const legBristles: Phaser.GameObjects.GameObject[] = [];
  const legSpec = [
    { kx: -2, ky: -2, mx: -12, my: -7, fx: -16, fy:  3, side: -1 },
    { kx: -2, ky: -1, mx: -14, my: -3, fx: -18, fy:  5, side: -1 },
    { kx: -2, ky:  1, mx: -14, my:  3, fx: -18, fy:  8, side: -1 },
    { kx: -2, ky:  2, mx: -12, my:  6, fx: -16, fy: 11, side: -1 },
    { kx:  2, ky: -2, mx:  12, my: -7, fx:  16, fy:  3, side:  1 },
    { kx:  2, ky: -1, mx:  14, my: -3, fx:  18, fy:  5, side:  1 },
    { kx:  2, ky:  1, mx:  14, my:  3, fx:  18, fy:  8, side:  1 },
    { kx:  2, ky:  2, mx:  12, my:  6, fx:  16, fy: 11, side:  1 },
  ];
  for (const L of legSpec) {
    const upper = scene.add.line(0, 0, L.kx, L.ky, L.mx, L.my, HAIR).setLineWidth(2).setOrigin(0, 0);
    const lower = scene.add.line(0, 0, L.mx, L.my, L.fx, L.fy, HAIR).setLineWidth(2).setOrigin(0, 0);
    legs.push(upper, lower);
    // Bristles on upper leg
    const ux = (L.kx + L.mx) / 2, uy = (L.ky + L.my) / 2;
    legBristles.push(
      scene.add.line(0, 0, ux,     uy,     ux + L.side * 0.4, uy - 2.2, HAIR_HI).setLineWidth(0.55).setOrigin(0, 0),
      scene.add.line(0, 0, ux + 1, uy - 1, ux + L.side * 1.6, uy - 1.8, HAIR_HI).setLineWidth(0.55).setOrigin(0, 0),
    );
    // Bristles on lower leg
    const lx = (L.mx + L.fx) / 2, ly = (L.my + L.fy) / 2;
    legBristles.push(
      scene.add.line(0, 0, lx,     ly,     lx + L.side * 0.6, ly + 1.8, HAIR_HI).setLineWidth(0.55).setOrigin(0, 0),
      scene.add.line(0, 0, lx + 1, ly,     lx + L.side * 2.0, ly + 0.4, HAIR_HI).setLineWidth(0.55).setOrigin(0, 0),
    );
    // Foot tip claw
    legBristles.push(
      scene.add.line(0, 0, L.fx, L.fy, L.fx + L.side * 0.8, L.fy + 1.2, HAIR).setLineWidth(0.7).setOrigin(0, 0),
    );
  }
  // Twitching leg animation
  scene.tweens.add({ targets: legs, scaleY: { from: 1, to: 0.92 }, yoyo: true, repeat: -1, duration: 280, ease: "Sine.easeInOut" });

  // ---- ABDOMEN — bulbous, hairy, blood-marked, breathing ----
  const abdomen = scene.add.ellipse(-6, 0, 18, 15, BODY_MID).setStrokeStyle(1.2, BODY_DK, 1);
  const abdomenShade = scene.add.ellipse(-6, 1, 13, 10, BODY_DK, 0.55);
  scene.tweens.add({ targets: [abdomen, abdomenShade], scaleX: { from: 1, to: 1.06 }, scaleY: { from: 1, to: 0.96 }, yoyo: true, repeat: -1, duration: 850, ease: "Sine.easeInOut" });

  // Blood-red tarantula-style markings
  const mark1 = scene.add.triangle(-6, -4, 0, 0, -3, 4, 3, 4, MARK);
  const mark2 = scene.add.triangle(-6, 4, 0, 0, -2.5, -3, 2.5, -3, MARK);
  const mark3 = scene.add.circle(-11, 0, 1.4, MARK, 0.85);

  // DENSE radial bristles around abdomen (16 hairs)
  const radialBristles: Phaser.GameObjects.GameObject[] = [];
  for (let i = 0; i < 16; i++) {
    const angle = (i / 16) * Math.PI * 2;
    const r1 = 7;
    const r2 = 10.5 + (i % 3 === 0 ? 1.8 : 0);
    const bx1 = -6 + Math.cos(angle) * r1;
    const by1 = Math.sin(angle) * r1 * 0.85;
    const bx2 = -6 + Math.cos(angle) * r2;
    const by2 = Math.sin(angle) * r2 * 0.85;
    radialBristles.push(scene.add.line(0, 0, bx1, by1, bx2, by2, HAIR).setLineWidth(0.7).setOrigin(0, 0));
  }
  // Extra long spiky tufts standing up from abdomen
  const tuft1 = scene.add.line(0, 0, -8,  -5, -8,  -10, HAIR).setLineWidth(0.95).setOrigin(0, 0);
  const tuft2 = scene.add.line(0, 0, -5,  -6, -4,  -10, HAIR).setLineWidth(0.95).setOrigin(0, 0);
  const tuft3 = scene.add.line(0, 0, -10, -4, -12, -8,  HAIR).setLineWidth(0.95).setOrigin(0, 0);
  const tuft4 = scene.add.line(0, 0, -3,  -5, -1,  -9,  HAIR).setLineWidth(0.95).setOrigin(0, 0);
  const tuft5 = scene.add.line(0, 0, -13, -2, -16, -4,  HAIR).setLineWidth(0.85).setOrigin(0, 0);

  // ---- CEPHALOTHORAX — front body, also hairy ----
  const ceph = scene.add.ellipse(5, 0, 13, 11, BODY_MID).setStrokeStyle(1.2, BODY_DK, 1);
  const cephHi = scene.add.ellipse(5, -2, 8, 5, BODY_HI, 0.55);
  // Hair tufts on cephalothorax (top + bottom)
  const cTuft1 = scene.add.line(0, 0, 2, -5,   1, -8.5, HAIR).setLineWidth(0.7).setOrigin(0, 0);
  const cTuft2 = scene.add.line(0, 0, 5, -5.5, 5, -8.5, HAIR).setLineWidth(0.7).setOrigin(0, 0);
  const cTuft3 = scene.add.line(0, 0, 8, -5,   9, -8.5, HAIR).setLineWidth(0.7).setOrigin(0, 0);
  const cTuft4 = scene.add.line(0, 0, 3,  5,   2,  7.5, HAIR).setLineWidth(0.7).setOrigin(0, 0);
  const cTuft5 = scene.add.line(0, 0, 7,  5,   8,  7.5, HAIR).setLineWidth(0.7).setOrigin(0, 0);

  // ---- 8 RED EYES (creepy cluster, pulsing glow) ----
  const eyeGlow = scene.add.circle(8, -1, 6, EYE_RED, 0.35);
  scene.tweens.add({ targets: eyeGlow, alpha: { from: 0.15, to: 0.55 }, yoyo: true, repeat: -1, duration: 480 });
  const eyeT1 = scene.add.circle( 6, -3,   1.0, EYE_RED).setStrokeStyle(0.4, 0x000, 1);
  const eyeT2 = scene.add.circle( 8, -3.4, 1.2, EYE_RED).setStrokeStyle(0.4, 0x000, 1);
  const eyeT3 = scene.add.circle(10, -3,   1.0, EYE_RED).setStrokeStyle(0.4, 0x000, 1);
  const eyeT4 = scene.add.circle(12, -2.4, 0.85, EYE_RED).setStrokeStyle(0.4, 0x000, 1);
  const eyeB1 = scene.add.circle( 6,  0,   0.75, EYE_RED).setStrokeStyle(0.3, 0x000, 1);
  const eyeB2 = scene.add.circle( 8,  0.6, 0.9,  EYE_RED).setStrokeStyle(0.3, 0x000, 1);
  const eyeB3 = scene.add.circle(10,  0,   0.75, EYE_RED).setStrokeStyle(0.3, 0x000, 1);
  const eyeB4 = scene.add.circle(12,  0.4, 0.65, EYE_RED).setStrokeStyle(0.3, 0x000, 1);
  // Reflective eye shines
  const eyeShine1 = scene.add.circle(8.4, -3.8, 0.4,  0xffffff, 0.95);
  const eyeShine2 = scene.add.circle(10.4, -3.3, 0.35, 0xffffff, 0.9);

  // ---- CHELICERAE (fang-jaws), pedipalps, large dripping fangs ----
  const jawL = scene.add.ellipse(11,  2.6, 3, 6, BODY_DK).setStrokeStyle(0.6, 0x000, 1);
  const jawR = scene.add.ellipse(11, -2.6, 3, 6, BODY_DK).setStrokeStyle(0.6, 0x000, 1);
  // Hair on jaws
  const jawHair1 = scene.add.line(0, 0, 11,  4,  11,  7, HAIR).setLineWidth(0.6).setOrigin(0, 0);
  const jawHair2 = scene.add.line(0, 0, 11, -4,  11, -7, HAIR).setLineWidth(0.6).setOrigin(0, 0);
  // Pedipalps (small antennae-like appendages near mouth)
  const palpL = scene.add.line(0, 0, 11,  3, 14.5,  3.6, BODY_DK).setLineWidth(1.2).setOrigin(0, 0);
  const palpR = scene.add.line(0, 0, 11, -3, 14.5, -3.6, BODY_DK).setLineWidth(1.2).setOrigin(0, 0);
  // Big curved fangs
  const fangL = scene.add.triangle(13,  5, 0, 0, -1.1, 5.5, 1.1, 5.5, FANG).setStrokeStyle(0.5, 0x4a3018, 1);
  const fangR = scene.add.triangle(13, -5, 0, 0, -1.1, 5.5, 1.1, 5.5, FANG).setStrokeStyle(0.5, 0x4a3018, 1);
  // Blood smear on fang tips
  const fangTipL = scene.add.circle(13,  10, 0.6, MARK);
  const fangTipR = scene.add.circle(13, -10, 0.6, MARK);
  // Venom drips
  const venomL = scene.add.circle(13,  11, 0.7, VENOM);
  const venomR = scene.add.circle(13, -11, 0.7, VENOM);
  scene.tweens.add({ targets: venomL, y: 14, alpha: 0, yoyo: true, repeat: -1, duration: 750 });
  scene.tweens.add({ targets: venomR, y: -14, alpha: 0, yoyo: true, repeat: -1, duration: 800, delay: 200 });

  c.add([
    shadow, silk,
    ...legs,
    ...legBristles,
    abdomen, abdomenShade,
    mark1, mark2, mark3,
    ...radialBristles,
    tuft1, tuft2, tuft3, tuft4, tuft5,
    ceph, cephHi,
    cTuft1, cTuft2, cTuft3, cTuft4, cTuft5,
    palpL, palpR,
    eyeGlow,
    eyeT1, eyeT2, eyeT3, eyeT4,
    eyeB1, eyeB2, eyeB3, eyeB4,
    eyeShine1, eyeShine2,
    jawL, jawR, jawHair1, jawHair2,
    fangL, fangR, fangTipL, fangTipR,
    venomL, venomR,
  ]);
}

function buildTank(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // Cursed Minotaur slaver — chains, skull pauldrons, blood-stained battleaxe.
  const shadow = scene.add.ellipse(0, 18, 30, 8, 0x000000, 0.55);
  // Legs (sturdy)
  const legL = scene.add.rectangle(-6, 13, 6, 10, 0x3a2010).setStrokeStyle(1, 0x0a0500, 1);
  const legR = scene.add.rectangle(6, 13, 6, 10, 0x3a2010).setStrokeStyle(1, 0x0a0500, 1);
  // Hooves
  const hoofL = scene.add.rectangle(-6, 19, 8, 4, 0x0a0500).setStrokeStyle(1, 0x000, 1);
  const hoofR = scene.add.rectangle(6, 19, 8, 4, 0x0a0500).setStrokeStyle(1, 0x000, 1);
  // Ankle chains — small linked ovals
  const chain1 = scene.add.ellipse(-6, 17, 3, 1.4, 0xb0b0b8).setStrokeStyle(0.5, 0x000, 1);
  const chain2 = scene.add.ellipse(6, 17, 3, 1.4, 0xb0b0b8).setStrokeStyle(0.5, 0x000, 1);
  // Bloody loincloth
  const cloth = scene.add.rectangle(0, 9, 18, 6, 0x4a2008).setStrokeStyle(1, 0x1a0808, 1);
  const clothBlood = scene.add.circle(-3, 9, 1.4, 0x6a0000, 0.85);
  // Torso (broad chest, dark brown)
  const torso = scene.add.ellipse(0, 0, 24, 18, 0x5a3818).setStrokeStyle(2, 0x1a0e00, 1);
  // Chest plate (rusted iron)
  const chestPlate = scene.add.rectangle(0, 2, 18, 12, 0x2a1a0a).setStrokeStyle(1, 0x000, 1);
  // Rust streaks on chest plate
  const rust1 = scene.add.line(0, 0, -6, -2, -4, 5, 0x7a3000, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  const rust2 = scene.add.line(0, 0, 4, -3, 6, 4, 0x7a3000, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  // Iron-spiked belt
  const belt = scene.add.rectangle(0, 8, 22, 2.4, 0x0a0500);
  const beltSpike1 = scene.add.triangle(-7, 8, 0, 0, -1, -2, 1, -2, 0x9a9aa0);
  const beltSpike2 = scene.add.triangle(0, 8, 0, 0, -1, -2, 1, -2, 0x9a9aa0);
  const beltSpike3 = scene.add.triangle(7, 8, 0, 0, -1, -2, 1, -2, 0x9a9aa0);
  // Skull pauldrons — bone caps on shoulders
  const padSkullL = scene.add.circle(-12, -7, 4.5, 0xeae0c0).setStrokeStyle(1, 0x2a1a08, 1);
  const padSkullR = scene.add.circle(12, -7, 4.5, 0xeae0c0).setStrokeStyle(1, 0x2a1a08, 1);
  const skullEyeL1 = scene.add.circle(-13, -7, 0.8, 0x000);
  const skullEyeL2 = scene.add.circle(-11, -7, 0.8, 0x000);
  const skullEyeR1 = scene.add.circle(11, -7, 0.8, 0x000);
  const skullEyeR2 = scene.add.circle(13, -7, 0.8, 0x000);
  const skullJawL = scene.add.rectangle(-12, -4, 3.5, 1, 0x000);
  const skullJawR = scene.add.rectangle(12, -4, 3.5, 1, 0x000);
  // Arms
  const armL = scene.add.rectangle(-13, 1, 4, 12, 0x5a3818).setStrokeStyle(1, 0x1a0e00, 1);
  const armR = scene.add.rectangle(13, 1, 4, 12, 0x5a3818).setStrokeStyle(1, 0x1a0e00, 1);
  // Chain dangling from left wrist
  const chainW1 = scene.add.ellipse(-13, 11, 2, 1, 0xb0b0b8);
  const chainW2 = scene.add.ellipse(-13, 13, 2, 1, 0xb0b0b8);
  const chainW3 = scene.add.ellipse(-13, 15, 2, 1, 0xb0b0b8);
  const fistL = scene.add.circle(-13, 8, 2.6, 0x5a3818).setStrokeStyle(1, 0x1a0e00, 1);
  const fistR = scene.add.circle(13, 8, 2.6, 0x5a3818).setStrokeStyle(1, 0x1a0e00, 1);
  // Bloody battleaxe in right hand
  const axeShaft = scene.add.rectangle(15, 4, 1.5, 14, 0x2a1a08);
  const axeBlade = scene.add.triangle(18, -1, 0, 0, 6, -3, 6, 5, 0x8a8a90).setStrokeStyle(1, 0x000, 1);
  const axeBlood = scene.add.triangle(20, 3, 0, 0, -1, 4, 1, 4, 0x6a0000);
  // Head with bovine snout
  const head = scene.add.ellipse(0, -10, 14, 11, 0x5a3818).setStrokeStyle(1, 0x1a0e00, 1);
  const snout = scene.add.ellipse(0, -8, 9, 6, 0x7a5838).setStrokeStyle(1, 0x1a0e00, 1);
  // Big curved blood-tipped horns
  const hornL = scene.add.ellipse(-10, -14, 5, 10, 0xeae0c0).setStrokeStyle(1, 0x4a3018, 1);
  hornL.setRotation(-0.7);
  const hornR = scene.add.ellipse(10, -14, 5, 10, 0xeae0c0).setStrokeStyle(1, 0x4a3018, 1);
  hornR.setRotation(0.7);
  // Blood on horn tips
  const hornBloodL = scene.add.circle(-13, -18, 1.2, 0x6a0000);
  const hornBloodR = scene.add.circle(13, -18, 1.2, 0x6a0000);
  // Top of head fur
  const tuft = scene.add.ellipse(0, -16, 12, 5, 0x2a1a08).setStrokeStyle(1, 0x000, 1);
  // Glowing red rage eyes
  const eyeGlow = scene.add.circle(0, -11, 5, 0xff2020, 0.3);
  scene.tweens.add({ targets: eyeGlow, alpha: 0.05, yoyo: true, repeat: -1, duration: 750 });
  const eyeL = scene.add.circle(-3, -11, 1.4, 0xff3030);
  const eyeR = scene.add.circle(3, -11, 1.4, 0xff3030);
  // Nose ring
  const nose = scene.add.circle(0, -6, 1.6, 0x000);
  const noseRing = scene.add.circle(0, -4.5, 1.4, 0xa0a0a8, 0).setStrokeStyle(0.7, 0xc0c0c8, 1);
  // Drool / froth from mouth
  const drool = scene.add.circle(2, -4, 0.7, 0xeeeeee, 0.85);
  scene.tweens.add({ targets: drool, y: -2, alpha: 0, yoyo: true, repeat: -1, duration: 900 });
  c.add([
    shadow, legL, legR, hoofL, hoofR, chain1, chain2,
    cloth, clothBlood,
    torso, chestPlate, rust1, rust2,
    belt, beltSpike1, beltSpike2, beltSpike3,
    armL, armR, chainW1, chainW2, chainW3, fistL, fistR,
    axeShaft, axeBlade, axeBlood,
    padSkullL, padSkullR, skullEyeL1, skullEyeL2, skullEyeR1, skullEyeR2, skullJawL, skullJawR,
    head, snout, hornL, hornR, hornBloodL, hornBloodR, tuft,
    eyeGlow, eyeL, eyeR, nose, noseRing, drool,
  ]);
}

function buildBoss(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // Reaper Pit Lord — red demon with bat wings, soul-eater scythe, glowing aura.
  const aura = scene.add.circle(0, 2, 32, 0xff1a1a, 0.28);
  scene.tweens.add({ targets: aura, scale: 1.4, alpha: 0.1, yoyo: true, repeat: -1, duration: 900 });
  const shadow = scene.add.ellipse(0, 22, 50, 13, 0x000000, 0.65);

  // Bat wings — drawn behind body with bone struts.
  const wingL = scene.add.polygon(-15, -6, [
    0, 0,
    -16, -8,
    -22, -2,
    -20, 4,
    -12, 6,
    -6, 4,
  ], 0x300808).setStrokeStyle(1.5, 0x000, 1);
  const wingR = scene.add.polygon(15, -6, [
    0, 0,
    16, -8,
    22, -2,
    20, 4,
    12, 6,
    6, 4,
  ], 0x300808).setStrokeStyle(1.5, 0x000, 1);
  // Wing bone struts
  const bone1L = scene.add.line(0, 0, -15, -6, -22, -2, 0x6a2020).setLineWidth(0.8).setOrigin(0, 0);
  const bone2L = scene.add.line(0, 0, -15, -6, -20, 4, 0x6a2020).setLineWidth(0.8).setOrigin(0, 0);
  const bone1R = scene.add.line(0, 0, 15, -6, 22, -2, 0x6a2020).setLineWidth(0.8).setOrigin(0, 0);
  const bone2R = scene.add.line(0, 0, 15, -6, 20, 4, 0x6a2020).setLineWidth(0.8).setOrigin(0, 0);
  // Wing flap animation
  scene.tweens.add({ targets: [wingL, wingR], scaleY: 0.8, yoyo: true, repeat: -1, duration: 380, ease: "Sine.easeInOut" });
  // Demon legs (cloven hooves)
  const legL = scene.add.rectangle(-7, 16, 6, 10, 0x6a1010).setStrokeStyle(1, 0x300000, 1);
  const legR = scene.add.rectangle(7, 16, 6, 10, 0x6a1010).setStrokeStyle(1, 0x300000, 1);
  const hoofL = scene.add.triangle(-7, 22, 0, -3, -4, 4, 4, 4, 0x1a0000).setStrokeStyle(1, 0x000, 1);
  const hoofR = scene.add.triangle(7, 22, 0, -3, -4, 4, 4, 4, 0x1a0000).setStrokeStyle(1, 0x000, 1);
  // Loincloth with skull belt
  const cloth = scene.add.rectangle(0, 11, 22, 7, 0x4a0a00).setStrokeStyle(1, 0x000, 1);
  const skull = scene.add.circle(0, 11, 2.6, 0xeae4d4).setStrokeStyle(1, 0x000, 1);
  // Torso (massive)
  const torso = scene.add.ellipse(0, 0, 30, 22, 0xa02020).setStrokeStyle(2, 0x400808, 1);
  // Chest stripes (battle scars)
  const scar1 = scene.add.rectangle(-5, -2, 8, 1.4, 0x6a0808);
  scar1.setRotation(-0.5);
  const scar2 = scene.add.rectangle(5, 2, 6, 1.4, 0x6a0808);
  scar2.setRotation(0.5);
  // Spiked shoulder pads
  const padL = scene.add.triangle(-14, -6, 0, 6, 8, -2, 16, 6, 0x4a1010).setStrokeStyle(1, 0x000, 1);
  const padR = scene.add.triangle(14, -6, 0, 6, 8, -2, 16, 6, 0x4a1010).setStrokeStyle(1, 0x000, 1);
  // Spikes on shoulder pads
  const spk1 = scene.add.triangle(-14, -10, 0, -3, -2, 2, 2, 2, 0x000).setStrokeStyle(1, 0xfff0e0, 0.5);
  const spk2 = scene.add.triangle(14, -10, 0, -3, -2, 2, 2, 2, 0x000).setStrokeStyle(1, 0xfff0e0, 0.5);
  // Arms (muscular)
  const armL = scene.add.rectangle(-16, 2, 5, 14, 0xa02020).setStrokeStyle(1, 0x400808, 1);
  const armR = scene.add.rectangle(16, 2, 5, 14, 0xa02020).setStrokeStyle(1, 0x400808, 1);
  // Clawed hands
  const handL = scene.add.circle(-16, 10, 3, 0xa02020).setStrokeStyle(1, 0x400808, 1);
  const handR = scene.add.circle(16, 10, 3, 0xa02020).setStrokeStyle(1, 0x400808, 1);
  const clawL = scene.add.triangle(-18, 13, 0, 4, -1, 0, 1, 0, 0xfff0e0);
  const clawR = scene.add.triangle(18, 13, 0, 4, -1, 0, 1, 0, 0xfff0e0);
  // Head
  const head = scene.add.ellipse(0, -14, 14, 12, 0xa02020).setStrokeStyle(1, 0x400808, 1);
  // Massive curved demon horns
  const hornL = scene.add.ellipse(-10, -22, 5, 14, 0x2a0808).setStrokeStyle(2, 0x000, 1);
  hornL.setRotation(-0.5);
  const hornR = scene.add.ellipse(10, -22, 5, 14, 0x2a0808).setStrokeStyle(2, 0x000, 1);
  hornR.setRotation(0.5);
  // Glowing red soul-eater eyes
  const eyeHaloL = scene.add.circle(-4, -14, 4, 0xff2020, 0.4);
  const eyeHaloR = scene.add.circle(4, -14, 4, 0xff2020, 0.4);
  const eyeL = scene.add.circle(-4, -14, 2.4, 0xff3030);
  const eyeR = scene.add.circle(4, -14, 2.4, 0xff3030);
  const pupL = scene.add.circle(-4, -14, 1, 0xfff0e0);
  const pupR = scene.add.circle(4, -14, 1, 0xfff0e0);
  scene.tweens.add({ targets: [eyeL, eyeR, eyeHaloL, eyeHaloR], alpha: 0.5, yoyo: true, repeat: -1, duration: 600 });
  // Snarling mouth with multiple fangs
  const mouth = scene.add.rectangle(0, -10, 10, 3, 0x1a0000);
  const fangL = scene.add.triangle(-3, -8, 0, 0, -1.4, 4, 1.4, 0, 0xfff0e0).setStrokeStyle(1, 0x6a4a00, 1);
  const fangR = scene.add.triangle(3, -8, 0, 0, -1.4, 4, 1.4, 0, 0xfff0e0).setStrokeStyle(1, 0x6a4a00, 1);
  const fangM = scene.add.triangle(0, -8, 0, 0, -1, 3, 1, 0, 0xfff0e0).setStrokeStyle(0.7, 0x6a4a00, 1);
  // Blood drip from mouth
  const mouthBlood = scene.add.circle(2, -7, 0.8, 0x6a0000);
  scene.tweens.add({ targets: mouthBlood, y: -3, alpha: 0, yoyo: true, repeat: -1, duration: 1100 });

  // SOUL-EATER SCYTHE — held in right hand, blade curving up + behind.
  const scytheShaft = scene.add.rectangle(20, 4, 1.8, 28, 0x1a0a00).setStrokeStyle(0.6, 0x000, 1);
  scytheShaft.setRotation(-0.18);
  // Curved blade — built from triangle + arc-style polygon
  const scytheBlade = scene.add.polygon(20, -10, [
    0, 0,
    14, -4,
    18, 0,
    14, 4,
    6, 2,
    2, 4,
  ], 0xc8d8e8).setStrokeStyle(1.2, 0x000, 1);
  // Blood-stained edge of blade
  const scytheEdge = scene.add.polygon(20, -10, [
    0, 0,
    14, -4,
    18, 0,
    14, 0,
    2, 0,
  ], 0x8a0000, 0.7);
  // Soul wisp on blade tip — green glow
  const soulWisp = scene.add.circle(38, -10, 3, 0x55ff88, 0.55);
  scene.tweens.add({ targets: soulWisp, alpha: 0.15, scale: 1.4, yoyo: true, repeat: -1, duration: 700 });
  // Bone wrap on shaft
  const shaftWrap1 = scene.add.rectangle(20, 0, 3, 1.2, 0xeae0c0);
  const shaftWrap2 = scene.add.rectangle(20, 8, 3, 1.2, 0xeae0c0);

  c.add([
    aura, wingL, wingR, bone1L, bone2L, bone1R, bone2R,
    shadow, legL, legR, hoofL, hoofR, cloth, skull,
    torso, scar1, scar2, padL, padR, spk1, spk2,
    armL, armR, handL, handR, clawL, clawR,
    scytheShaft, shaftWrap1, shaftWrap2, scytheBlade, scytheEdge, soulWisp,
    head, hornL, hornR,
    eyeHaloL, eyeHaloR, eyeL, eyeR, pupL, pupR,
    mouth, fangL, fangM, fangR, mouthBlood,
  ]);
}

function buildElite(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // Wailing GHOST — translucent pale-blue wraith. Hooded shroud, no legs,
  // tattered cloak fading into wisps, glowing eyes in the void, rusted chains.
  const shadow = scene.add.ellipse(0, 16, 22, 5, 0x000000, 0.4);
  // Floating ectoplasm aura around the wraith
  const aura = scene.add.ellipse(0, 0, 30, 32, 0x88ccff, 0.22);
  scene.tweens.add({ targets: aura, alpha: { from: 0.12, to: 0.32 }, scale: { from: 0.95, to: 1.15 }, yoyo: true, repeat: -1, duration: 900 });

  // Floating bob (ghost has no legs — it hovers above the ground)
  scene.tweens.add({ targets: c, y: "+=2", yoyo: true, repeat: -1, duration: 1300, ease: "Sine.easeInOut" });

  // ---- TATTERED LOWER SHROUD (replaces legs — fades into ragged wisps) ----
  const shroudBase = scene.add.polygon(0, 8, [
    -10, -2,
     10, -2,
    12, 6,
    9, 9,
    6, 7,
    3, 10,
    0, 7,
    -3, 10,
    -6, 7,
    -9, 9,
    -12, 6,
  ], 0x6a8acc, 0.85).setStrokeStyle(0.8, 0x2a4a8a, 1);
  // Translucent wisps trailing below — drifting/fading
  const wisp1 = scene.add.ellipse(-6, 14, 4, 4, 0x88ccff, 0.4);
  const wisp2 = scene.add.ellipse(0, 16, 5, 5, 0x88ccff, 0.35);
  const wisp3 = scene.add.ellipse(6, 14, 4, 4, 0x88ccff, 0.4);
  scene.tweens.add({ targets: [wisp1, wisp2, wisp3], alpha: { from: 0.15, to: 0.6 }, scale: { from: 0.7, to: 1.2 }, yoyo: true, repeat: -1, duration: 700 });

  // ---- TORSO/SHROUD (translucent body) ----
  const torso = scene.add.ellipse(0, 0, 18, 16, 0x88ccff, 0.7).setStrokeStyle(1, 0x4a6a9a, 0.9);
  // Inner darker void
  const innerVoid = scene.add.ellipse(0, 1, 11, 11, 0x1a2a4a, 0.65);
  // Translucent body shimmer
  scene.tweens.add({ targets: torso, alpha: { from: 0.55, to: 0.85 }, yoyo: true, repeat: -1, duration: 1100 });

  // ---- RUSTED HANGING CHAINS (signature ghost iconography) ----
  // Chain on left side — short hanging segments
  const chainL1 = scene.add.ellipse(-9, -1, 1.6, 2.4, 0x6a4a3a).setStrokeStyle(0.5, 0x2a1a08, 1);
  const chainL2 = scene.add.ellipse(-9.5, 2, 1.6, 2.4, 0x8a6a4a).setStrokeStyle(0.5, 0x2a1a08, 1);
  const chainL3 = scene.add.ellipse(-9, 5, 1.6, 2.4, 0x6a4a3a).setStrokeStyle(0.5, 0x2a1a08, 1);
  const chainL4 = scene.add.ellipse(-9.5, 8, 1.6, 2.4, 0x8a6a4a).setStrokeStyle(0.5, 0x2a1a08, 1);
  // Chain on right side
  const chainR1 = scene.add.ellipse(9, -1, 1.6, 2.4, 0x6a4a3a).setStrokeStyle(0.5, 0x2a1a08, 1);
  const chainR2 = scene.add.ellipse(9.5, 2, 1.6, 2.4, 0x8a6a4a).setStrokeStyle(0.5, 0x2a1a08, 1);
  const chainR3 = scene.add.ellipse(9, 5, 1.6, 2.4, 0x6a4a3a).setStrokeStyle(0.5, 0x2a1a08, 1);
  // Chains sway slightly
  scene.tweens.add({ targets: [chainL1, chainL2, chainL3, chainL4], x: "+=0.6", yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [chainR1, chainR2, chainR3], x: "-=0.6", yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });

  // ---- ARMS — wispy translucent claw-hands reaching out ----
  const armL = scene.add.ellipse(-7, 2, 4, 7, 0x88ccff, 0.7).setStrokeStyle(0.6, 0x4a6a9a, 0.8);
  armL.setRotation(-0.5);
  const armR = scene.add.ellipse(7, 2, 4, 7, 0x88ccff, 0.7).setStrokeStyle(0.6, 0x4a6a9a, 0.8);
  armR.setRotation(0.5);
  // Skeletal claw fingers at the ends
  const handL = scene.add.circle(-10, 5, 1.6, 0xeae0c0, 0.95).setStrokeStyle(0.5, 0x4a3018, 1);
  const handR = scene.add.circle(10, 5, 1.6, 0xeae0c0, 0.95).setStrokeStyle(0.5, 0x4a3018, 1);
  const clawL1 = scene.add.line(0, 0, -10, 5, -12, 8, 0xeae0c0, 0.9).setLineWidth(0.7).setOrigin(0, 0);
  const clawL2 = scene.add.line(0, 0, -10, 5, -11, 9, 0xeae0c0, 0.9).setLineWidth(0.7).setOrigin(0, 0);
  const clawR1 = scene.add.line(0, 0, 10, 5, 12, 8, 0xeae0c0, 0.9).setLineWidth(0.7).setOrigin(0, 0);
  const clawR2 = scene.add.line(0, 0, 10, 5, 11, 9, 0xeae0c0, 0.9).setLineWidth(0.7).setOrigin(0, 0);

  // ---- HOODED HEAD — empty void inside the hood ----
  // Hood (covers most of head — pale blue translucent shroud)
  const hood = scene.add.polygon(0, -11, [
    -7, 4,
    -8, -2,
    -6, -7,
    -2, -9,
     2, -9,
     6, -7,
     8, -2,
     7, 4,
  ], 0x4a6a9a, 0.85).setStrokeStyle(0.9, 0x1a2a4a, 1);
  // Empty void inside hood (deep dark)
  const hoodVoid = scene.add.ellipse(0, -10, 9, 9, 0x000, 0.95);
  // Two glowing pale-blue eyes in the void (signature ghost)
  const eyeGlowL = scene.add.circle(-2.4, -10, 1.8, 0xb6e6ff, 0.55);
  const eyeGlowR = scene.add.circle(2.4, -10, 1.8, 0xb6e6ff, 0.55);
  const eyeL = scene.add.circle(-2.4, -10, 1, 0xeaf6ff);
  const eyeR = scene.add.circle(2.4, -10, 1, 0xeaf6ff);
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR, eyeL, eyeR], alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 550 });
  // WAILING MOUTH — vertical oval gape with cold blue glow inside
  const mouth = scene.add.ellipse(0, -7, 2.2, 3.5, 0x1a2a4a).setStrokeStyle(0.5, 0x000, 1);
  const mouthGlow = scene.add.ellipse(0, -7, 1.6, 2.6, 0x88ccff, 0.6);
  scene.tweens.add({ targets: [mouth, mouthGlow], scaleY: { from: 0.7, to: 1.3 }, yoyo: true, repeat: -1, duration: 800, ease: "Sine.easeInOut" });
  // Breath wisp emerging from mouth
  const breathWisp = scene.add.circle(0, -5, 0.8, 0xeaf6ff, 0.7);
  scene.tweens.add({ targets: breathWisp, y: -1, alpha: 0, scale: 1.6, yoyo: true, repeat: -1, duration: 1000 });

  // Hood crown spikes (frostbite/curse spikes)
  const spikeL = scene.add.triangle(-5, -18, 0, 0, -1.4, 4, 1.4, 4, 0x88ccff, 0.85).setStrokeStyle(0.4, 0xffffff, 1).setRotation(-0.2);
  const spikeR = scene.add.triangle(5, -18, 0, 0, -1.4, 4, 1.4, 4, 0x88ccff, 0.85).setStrokeStyle(0.4, 0xffffff, 1).setRotation(0.2);
  // Cold breath wisp above hood
  const headWisp = scene.add.circle(0, -22, 1.2, 0xeaf6ff, 0.6);
  scene.tweens.add({ targets: headWisp, y: -28, alpha: 0, yoyo: true, repeat: -1, duration: 1200 });

  // ---- FREE-FLOATING SOUL ORBS orbiting the wraith ----
  const orb1 = scene.add.circle(-12, -4, 1.2, 0xb6e6ff, 0.85);
  const orb2 = scene.add.circle(12, 0, 1, 0xb6e6ff, 0.8);
  scene.tweens.add({ targets: orb1, y: { from: -4, to: 6 }, alpha: { from: 0.4, to: 0.95 }, yoyo: true, repeat: -1, duration: 1500 });
  scene.tweens.add({ targets: orb2, y: { from: 0, to: -8 }, alpha: { from: 0.4, to: 0.95 }, yoyo: true, repeat: -1, duration: 1300 });

  c.add([
    shadow, aura,
    wisp1, wisp2, wisp3,
    shroudBase,
    chainL1, chainL2, chainL3, chainL4, chainR1, chainR2, chainR3,
    torso, innerVoid,
    armL, armR, handL, handR, clawL1, clawL2, clawR1, clawR2,
    hood, hoodVoid,
    eyeGlowL, eyeGlowR, eyeL, eyeR,
    mouth, mouthGlow, breathWisp,
    spikeL, spikeR, headWisp,
    orb1, orb2,
  ]);
}

function buildTitan(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // Hellforged stone titan — magma-cursed granite, glowing red-orange seams, chest skull idol.
  const aura = scene.add.circle(0, 0, 34, 0xff4400, 0.22);
  scene.tweens.add({ targets: aura, scale: 1.25, alpha: 0.08, yoyo: true, repeat: -1, duration: 1100 });

  const shadow = scene.add.ellipse(0, 22, 50, 12, 0x000000, 0.65);

  // Massive stone legs — darker volcanic granite.
  const legL = scene.add.rectangle(-7, 14, 9, 12, 0x4a4a4a).setStrokeStyle(1.5, 0x1a1a1a, 1);
  const legR = scene.add.rectangle(7, 14, 9, 12, 0x4a4a4a).setStrokeStyle(1.5, 0x1a1a1a, 1);
  // Stone foot slabs.
  const footL = scene.add.rectangle(-7, 21, 13, 5, 0x2a2a2a).setStrokeStyle(1, 0x000, 1);
  const footR = scene.add.rectangle(7, 21, 13, 5, 0x2a2a2a).setStrokeStyle(1, 0x000, 1);
  // Magma seams on legs (orange/red glow).
  const crackL = scene.add.line(0, 0, -7, 10, -5, 20, 0xff5510, 0.95).setLineWidth(1.4).setOrigin(0, 0);
  const crackR = scene.add.line(0, 0,  7, 10,  9, 20, 0xff5510, 0.95).setLineWidth(1.4).setOrigin(0, 0);

  // Broad stone torso — dark slab.
  const torso = scene.add.rectangle(0, 0, 28, 20, 0x5a5a5a).setStrokeStyle(2, 0x1a1a1a, 1);
  // Chest cracks (lava lines across chest).
  const chestCrack1 = scene.add.line(0, 0, -8, -2, 4, 4, 0xff6020, 0.95).setLineWidth(1.6).setOrigin(0, 0);
  const chestCrack2 = scene.add.line(0, 0,  2, -4, 10, 2, 0xff6020, 0.85).setLineWidth(1.2).setOrigin(0, 0);
  // Pulse magma cracks
  scene.tweens.add({ targets: [chestCrack1, chestCrack2, crackL, crackR], alpha: 0.55, yoyo: true, repeat: -1, duration: 850 });
  // Carved skull idol on chest — bone-colored.
  const idolBg = scene.add.circle(0, 1, 5, 0x2a2a2a).setStrokeStyle(1, 0x000, 1);
  const chestSkull = scene.add.circle(0, 1, 4, 0xeae0c0).setStrokeStyle(0.8, 0x000, 1);
  const chestSkullEye1 = scene.add.circle(-1.4, 0.3, 1, 0xff2020);
  const chestSkullEye2 = scene.add.circle(1.4, 0.3, 1, 0xff2020);
  scene.tweens.add({ targets: [chestSkullEye1, chestSkullEye2], alpha: 0.5, yoyo: true, repeat: -1, duration: 600 });
  const chestSkullJaw = scene.add.rectangle(0, 3, 3, 1.2, 0x000);

  // Massive arms — stone slabs.
  const armL = scene.add.rectangle(-17, 2, 7, 16, 0x4a4a4a).setStrokeStyle(1.5, 0x1a1a1a, 1);
  const armR = scene.add.rectangle(17, 2, 7, 16, 0x4a4a4a).setStrokeStyle(1.5, 0x1a1a1a, 1);
  // Magma cracks on arms
  const armCrackL = scene.add.line(0, 0, -17, -2, -17, 8, 0xff5510, 0.85).setLineWidth(0.9).setOrigin(0, 0);
  const armCrackR = scene.add.line(0, 0, 17, -2, 17, 8, 0xff5510, 0.85).setLineWidth(0.9).setOrigin(0, 0);
  // Fists — flat stone blocks with knuckle spikes.
  const fistL = scene.add.rectangle(-17, 12, 10, 8, 0x3a3a3a).setStrokeStyle(1, 0x000, 1);
  const fistR = scene.add.rectangle(17, 12, 10, 8, 0x3a3a3a).setStrokeStyle(1, 0x000, 1);
  const fistSpikeL1 = scene.add.triangle(-19, 16, 0, 0, -1, 3, 1, 3, 0xeae0c0);
  const fistSpikeL2 = scene.add.triangle(-15, 16, 0, 0, -1, 3, 1, 3, 0xeae0c0);
  const fistSpikeR1 = scene.add.triangle(15, 16, 0, 0, -1, 3, 1, 3, 0xeae0c0);
  const fistSpikeR2 = scene.add.triangle(19, 16, 0, 0, -1, 3, 1, 3, 0xeae0c0);

  // Head — rectangular stone block, no neck.
  const head = scene.add.rectangle(0, -13, 18, 14, 0x5a5a5a).setStrokeStyle(2, 0x1a1a1a, 1);
  // Glowing red rage eyes (deep magma, not lava-green).
  const eyeHaloL = scene.add.circle(-4, -13, 5, 0xff2010, 0.45);
  const eyeHaloR = scene.add.circle(4, -13, 5, 0xff2010, 0.45);
  const eyeL = scene.add.circle(-4, -13, 2.6, 0xff3030);
  const eyeR = scene.add.circle(4, -13, 2.6, 0xff3030);
  // Pupils (dark slits).
  const pupL = scene.add.rectangle(-4, -13, 0.8, 3, 0x1a0000);
  const pupR = scene.add.rectangle(4, -13, 0.8, 3, 0x1a0000);
  // Jagged stone mouth — horizontal crack with magma glow.
  const mouthGlow = scene.add.rectangle(0, -8, 11, 2.4, 0xff5010, 0.7);
  const mouth = scene.add.rectangle(0, -8, 10, 1.6, 0x1a0000);
  // Magma forehead crack.
  const headCrack = scene.add.line(0, 0, -1, -19, 2, -7, 0xff5010, 0.95).setLineWidth(1.4).setOrigin(0, 0);
  // Bone spikes on shoulders (pale, bone-like).
  const spikeL = scene.add.triangle(-17, -10, 0, 0, -3, -8, 3, -8, 0xeae0c0).setStrokeStyle(1, 0x4a4030, 1);
  const spikeR = scene.add.triangle(17, -10, 0, 0, -3, -8, 3, -8, 0xeae0c0).setStrokeStyle(1, 0x4a4030, 1);
  // Skull on top of head as a crown
  const crownSkull = scene.add.circle(0, -22, 2.4, 0xeae0c0).setStrokeStyle(0.7, 0x000, 1);
  const crownSkullEye1 = scene.add.circle(-0.8, -22, 0.5, 0x000);
  const crownSkullEye2 = scene.add.circle(0.8, -22, 0.5, 0x000);

  // Glow pulse on main eyes.
  scene.tweens.add({ targets: [eyeL, eyeR, eyeHaloL, eyeHaloR], alpha: 0.5, yoyo: true, repeat: -1, duration: 700 });

  c.add([
    aura, shadow,
    legL, legR, footL, footR, crackL, crackR,
    torso, chestCrack1, chestCrack2,
    idolBg, chestSkull, chestSkullEye1, chestSkullEye2, chestSkullJaw,
    armL, armR, armCrackL, armCrackR,
    fistL, fistR, fistSpikeL1, fistSpikeL2, fistSpikeR1, fistSpikeR2,
    spikeL, spikeR,
    head,
    eyeHaloL, eyeHaloR, eyeL, eyeR, pupL, pupR,
    mouthGlow, mouth, headCrack,
    crownSkull, crownSkullEye1, crownSkullEye2,
  ]);
}

// ---------- WRAITH (translucent skull ghost, hooded shroud) ----------
function buildWraith(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // BANSHEE — pure pale spirit. Bone-white tattered shroud, dark hollow eye
  // sockets, white wisp tail. No blue tinting — reads as a wailing ghost.
  const SHROUD_DK = 0x8a8a92;
  const SHROUD = 0xc8c6cc;
  const SHROUD_HI = 0xf4f2f6;
  const GLOW = 0xfaf8ff;
  const BONE = 0xfaf8f2;
  const BONE_DK = 0x9a9890;
  const EYE = 0xfff8e8;

  // Faint glow halo behind everything
  const aura = scene.add.circle(0, 0, 14, GLOW, 0.18);
  scene.tweens.add({ targets: aura, scale: { from: 1, to: 1.18 }, alpha: { from: 0.18, to: 0.08 }, yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });

  // Tattered shroud trailing behind (hood + ragged bottom)
  const shroudBack = scene.add.polygon(0, 4, [-9, -8, -11, 4, -7, 12, -3, 8, 0, 14, 3, 8, 7, 12, 11, 4, 9, -8], SHROUD_DK, 0.85).setStrokeStyle(0.6, 0x000, 0.7);
  const shroudMid = scene.add.polygon(0, 2, [-7, -6, -9, 4, -4, 9, 0, 11, 4, 9, 9, 4, 7, -6], SHROUD, 0.85);
  const shroudHi = scene.add.line(0, 0, -5, 0, 5, 0, SHROUD_HI, 0.6).setLineWidth(0.6).setOrigin(0, 0);

  // Hood opening shadow
  const hoodOpen = scene.add.ellipse(0, -6, 11, 9, 0x000000, 0.55);

  // Skull head — translucent
  const skull = scene.add.circle(0, -7, 5.5, BONE, 0.92).setStrokeStyle(0.6, BONE_DK, 1);
  const skullCheek = scene.add.ellipse(0, -5, 6.5, 4, BONE, 0.7);

  // Hollow eye sockets with frost-glow
  const eyeGlowL = scene.add.circle(-2, -8, 1.6, EYE, 0.7);
  const eyeGlowR = scene.add.circle(2, -8, 1.6, EYE, 0.7);
  const eyeL = scene.add.circle(-2, -8, 0.9, 0x001020);
  const eyeR = scene.add.circle(2, -8, 0.9, 0x001020);
  const pupL = scene.add.circle(-2, -8, 0.4, EYE);
  const pupR = scene.add.circle(2, -8, 0.4, EYE);
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR], scale: { from: 0.9, to: 1.4 }, alpha: { from: 0.6, to: 0.95 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

  // Skeletal jaw with grin teeth
  const jaw = scene.add.rectangle(0, -4, 5, 1.6, BONE).setStrokeStyle(0.4, BONE_DK, 1);
  const tooth1 = scene.add.rectangle(-1.4, -3.5, 0.5, 1.2, 0x000);
  const tooth2 = scene.add.rectangle(0, -3.5, 0.5, 1.2, 0x000);
  const tooth3 = scene.add.rectangle(1.4, -3.5, 0.5, 1.2, 0x000);

  // Skeletal arm wisps (left + right) reaching forward
  const armL = scene.add.line(0, 0, -4, -2, -8, 4, BONE, 0.7).setLineWidth(0.9).setOrigin(0, 0);
  const armR = scene.add.line(0, 0, 4, -2, 8, 4, BONE, 0.7).setLineWidth(0.9).setOrigin(0, 0);
  const handL = scene.add.circle(-8, 4, 1.1, BONE, 0.75);
  const handR = scene.add.circle(8, 4, 1.1, BONE, 0.75);
  scene.tweens.add({ targets: [armL, handL], y: { from: 0, to: -1.5 }, yoyo: true, repeat: -1, duration: 880, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [armR, handR], y: { from: 0, to: -1.5 }, yoyo: true, repeat: -1, duration: 920, ease: "Sine.easeInOut" });

  // Wisp particles trailing behind (3 of them, fading)
  const wisp1 = scene.add.circle(-3, 12, 1.4, GLOW, 0.6);
  const wisp2 = scene.add.circle(0, 14, 1.0, GLOW, 0.5);
  const wisp3 = scene.add.circle(3, 12, 1.2, GLOW, 0.55);
  scene.tweens.add({ targets: wisp1, alpha: 0, y: "+=4", yoyo: true, repeat: -1, duration: 750, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: wisp2, alpha: 0, y: "+=5", yoyo: true, repeat: -1, duration: 880, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: wisp3, alpha: 0, y: "+=4", yoyo: true, repeat: -1, duration: 820, ease: "Sine.easeInOut" });

  // Float bob (independent of the makeCreep bob — gives a haunting drift)
  scene.tweens.add({ targets: c, y: { from: c.y, to: c.y - 1.2 }, yoyo: true, repeat: -1, duration: 1300, ease: "Sine.easeInOut" });

  c.add([
    aura,
    shroudBack, shroudMid, shroudHi,
    hoodOpen,
    skull, skullCheek,
    eyeGlowL, eyeGlowR, eyeL, eyeR, pupL, pupR,
    jaw, tooth1, tooth2, tooth3,
    armL, armR, handL, handR,
    wisp1, wisp2, wisp3,
  ]);
}

// ---------- HELLHOUND (burning quadruped, fire mane, ember trail) ----------
function buildHellhound(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  const COAL_DK = 0x100404;
  const COAL = 0x2a0a08;
  const COAL_HI = 0x4a1810;
  const FIRE = 0xff5010;
  const FIRE_HI = 0xffb030;
  const FIRE_CORE = 0xfff080;
  const EYE = 0xfff050;
  const FANG = 0xece4c0;

  const shadow = scene.add.ellipse(0, 11, 22, 4, 0x000000, 0.45);

  // Ember trail (behind body)
  const ember1 = scene.add.circle(-9, 6, 1.4, FIRE, 0.9);
  const ember2 = scene.add.circle(-12, 8, 1.0, FIRE_HI, 0.85);
  const ember3 = scene.add.circle(-7, 10, 0.8, FIRE_CORE, 0.95);
  scene.tweens.add({ targets: ember1, alpha: 0, x: "-=4", yoyo: true, repeat: -1, duration: 380, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: ember2, alpha: 0, x: "-=5", yoyo: true, repeat: -1, duration: 430, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: ember3, alpha: 0, x: "-=3", yoyo: true, repeat: -1, duration: 320, ease: "Sine.easeInOut" });

  // Tail (curving up + flame tip)
  const tail = scene.add.line(0, 0, -8, 2, -13, -3, COAL_HI, 1).setLineWidth(2).setOrigin(0, 0);
  const tailFire1 = scene.add.triangle(-13, -3, 0, 0, -3, -3, 3, -3, FIRE);
  const tailFire2 = scene.add.triangle(-13, -4, 0, 0, -2, -2, 2, -2, FIRE_HI);
  scene.tweens.add({ targets: [tailFire1, tailFire2], scaleY: { from: 1, to: 1.4 }, alpha: { from: 1, to: 0.7 }, yoyo: true, repeat: -1, duration: 240, ease: "Sine.easeInOut" });

  // Body — quadruped horizontal
  const body = scene.add.ellipse(0, 2, 18, 9, COAL).setStrokeStyle(0.7, 0x000, 1);
  const bodyHi = scene.add.ellipse(-1, 0, 12, 5, COAL_HI, 0.7);

  // 4 legs (front pair + back pair, with paws)
  const legFL = scene.add.rectangle(-5, 6, 2.2, 6, COAL_DK).setStrokeStyle(0.4, 0x000, 1);
  const legFR = scene.add.rectangle(-3, 6, 2.2, 6, COAL_DK).setStrokeStyle(0.4, 0x000, 1);
  const legBL = scene.add.rectangle(4, 6, 2.2, 6, COAL_DK).setStrokeStyle(0.4, 0x000, 1);
  const legBR = scene.add.rectangle(6, 6, 2.2, 6, COAL_DK).setStrokeStyle(0.4, 0x000, 1);
  const pawFL = scene.add.ellipse(-5, 9.5, 3, 1.5, COAL_DK);
  const pawFR = scene.add.ellipse(-3, 9.5, 3, 1.5, COAL_DK);
  const pawBL = scene.add.ellipse(4, 9.5, 3, 1.5, COAL_DK);
  const pawBR = scene.add.ellipse(6, 9.5, 3, 1.5, COAL_DK);
  scene.tweens.add({ targets: [legFL, legBR, pawFL, pawBR], y: "-=1.2", yoyo: true, repeat: -1, duration: 220, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [legFR, legBL, pawFR, pawBL], y: "-=1.2", yoyo: true, repeat: -1, duration: 220, ease: "Sine.easeInOut", delay: 110 });

  // Fire mane on back (3 flame tongues)
  const mane1 = scene.add.triangle(-2, -3, 0, 0, -2.5, -7, 2.5, -7, FIRE);
  const mane2 = scene.add.triangle(2, -4, 0, 0, -2.5, -8, 2.5, -8, FIRE);
  const mane3 = scene.add.triangle(5, -3, 0, 0, -2.5, -7, 2.5, -7, FIRE);
  const maneHi1 = scene.add.triangle(-2, -4, 0, 0, -1.4, -4, 1.4, -4, FIRE_HI);
  const maneHi2 = scene.add.triangle(2, -5, 0, 0, -1.4, -5, 1.4, -5, FIRE_CORE);
  const maneHi3 = scene.add.triangle(5, -4, 0, 0, -1.4, -4, 1.4, -4, FIRE_HI);
  scene.tweens.add({ targets: [mane1, mane2, mane3, maneHi1, maneHi2, maneHi3], scaleY: { from: 1, to: 1.45 }, alpha: { from: 1, to: 0.7 }, yoyo: true, repeat: -1, duration: 260, ease: "Sine.easeInOut" });

  // Head (forward, on right side since hound moves right)
  const head = scene.add.ellipse(9, 0, 9, 7, COAL).setStrokeStyle(0.7, 0x000, 1);
  const snout = scene.add.ellipse(13, 1, 5, 4, COAL_DK).setStrokeStyle(0.4, 0x000, 1);

  // Pointed ears
  const earL = scene.add.triangle(7, -5, 0, 0, -1.6, -3, 1.6, -3, COAL_DK);
  const earR = scene.add.triangle(10, -5, 0, 0, -1.6, -3, 1.6, -3, COAL_DK);
  const earInL = scene.add.triangle(7, -4, 0, 0, -0.7, -1.4, 0.7, -1.4, FIRE);
  const earInR = scene.add.triangle(10, -4, 0, 0, -0.7, -1.4, 0.7, -1.4, FIRE);

  // Glowing eyes
  const eyeGlow = scene.add.circle(10, -1, 1.6, EYE, 0.7);
  const eye = scene.add.circle(10, -1, 1.0, EYE);
  const eyePup = scene.add.line(0, 0, 10, -1.6, 10, -0.4, 0x300000, 1).setLineWidth(0.5).setOrigin(0, 0);
  scene.tweens.add({ targets: eyeGlow, scale: { from: 1, to: 1.4 }, yoyo: true, repeat: -1, duration: 420, ease: "Sine.easeInOut" });

  // Open snarling mouth + fangs
  const mouth = scene.add.rectangle(14, 2.5, 4, 1.2, FIRE).setStrokeStyle(0.4, 0x000, 1);
  const fang1 = scene.add.triangle(13, 3, 0, 0, -0.5, 1.4, 0.5, 1.4, FANG);
  const fang2 = scene.add.triangle(15, 3, 0, 0, -0.5, 1.4, 0.5, 1.4, FANG);

  // Fire breath puff
  const breath = scene.add.circle(17, 3, 1.5, FIRE_HI, 0.85);
  scene.tweens.add({ targets: breath, alpha: 0, x: "+=3", scale: 1.6, yoyo: true, repeat: -1, duration: 380, ease: "Sine.easeInOut" });

  c.add([
    shadow,
    ember1, ember2, ember3,
    tail, tailFire1, tailFire2,
    body, bodyHi,
    legFL, legFR, legBL, legBR,
    pawFL, pawFR, pawBL, pawBR,
    mane1, mane2, mane3, maneHi1, maneHi2, maneHi3,
    head, snout,
    earL, earR, earInL, earInR,
    eyeGlow, eye, eyePup,
    mouth, fang1, fang2,
    breath,
  ]);
}

// ---------- GOLEM (stone construct, glowing crystal heart) ----------
function buildGolem(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  const STONE_DK = 0x4a4030;
  const STONE = 0x6a5a44;
  const STONE_HI = 0x8a7a5a;
  const STONE_LT = 0xa89070;
  const CRACK = 0x261a10;
  const CRYSTAL = 0xff8030;
  const CRYSTAL_HI = 0xffd060;
  const EYE = 0xff5010;
  const MOSS = 0x4a7030;

  const shadow = scene.add.ellipse(0, 18, 28, 5, 0x000000, 0.5);

  // Massive blocky legs
  const legL = scene.add.rectangle(-7, 14, 7, 8, STONE_DK).setStrokeStyle(0.8, 0x000, 1);
  const legR = scene.add.rectangle(7, 14, 7, 8, STONE_DK).setStrokeStyle(0.8, 0x000, 1);
  const legHiL = scene.add.rectangle(-8, 13, 1.5, 6, STONE, 0.8);
  const legHiR = scene.add.rectangle(6, 13, 1.5, 6, STONE, 0.8);
  const footL = scene.add.rectangle(-7, 18, 9, 2, STONE_DK).setStrokeStyle(0.6, 0x000, 1);
  const footR = scene.add.rectangle(7, 18, 9, 2, STONE_DK).setStrokeStyle(0.6, 0x000, 1);

  // Boulder torso (trapezoid-ish, wider at top)
  const torso = scene.add.polygon(0, 4, [-12, -10, -14, 8, 14, 8, 12, -10], STONE).setStrokeStyle(1, 0x000, 1);
  const torsoHi = scene.add.polygon(-3, 0, [-7, -8, -9, 4, -2, 4, 0, -8], STONE_HI, 0.7);
  // Cracks on torso
  const crack1 = scene.add.line(0, 0, -8, -4, -3, 4, CRACK, 1).setLineWidth(0.7).setOrigin(0, 0);
  const crack2 = scene.add.line(0, 0, 4, -8, 6, 0, CRACK, 1).setLineWidth(0.7).setOrigin(0, 0);
  const crack3 = scene.add.line(0, 0, -2, 0, 8, 6, CRACK, 1).setLineWidth(0.7).setOrigin(0, 0);

  // Glowing crystal heart in chest
  const heartGlow = scene.add.circle(0, 1, 5.5, CRYSTAL, 0.55);
  const heart = scene.add.polygon(0, 1, [0, -3.5, 3, 0, 0, 3.5, -3, 0], CRYSTAL).setStrokeStyle(0.6, 0x602010, 1);
  const heartCore = scene.add.polygon(0, 1, [0, -1.6, 1.4, 0, 0, 1.6, -1.4, 0], CRYSTAL_HI);
  scene.tweens.add({ targets: heartGlow, scale: { from: 0.9, to: 1.25 }, alpha: { from: 0.4, to: 0.7 }, yoyo: true, repeat: -1, duration: 900, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: heartCore, alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 600, ease: "Sine.easeInOut" });

  // Massive stone arms
  const armL = scene.add.rectangle(-15, 4, 5, 14, STONE_DK).setStrokeStyle(0.7, 0x000, 1).setRotation(-0.15);
  const armR = scene.add.rectangle(15, 4, 5, 14, STONE_DK).setStrokeStyle(0.7, 0x000, 1).setRotation(0.15);
  const armHiL = scene.add.rectangle(-16, 4, 1.4, 12, STONE, 0.8).setRotation(-0.15);
  const armHiR = scene.add.rectangle(16, 4, 1.4, 12, STONE, 0.8).setRotation(0.15);

  // Big rock fists
  const fistL = scene.add.polygon(-17, 12, [-3, 0, -4, 4, 4, 4, 3, 0], STONE_HI).setStrokeStyle(0.7, 0x000, 1);
  const fistR = scene.add.polygon(17, 12, [-3, 0, -4, 4, 4, 4, 3, 0], STONE_HI).setStrokeStyle(0.7, 0x000, 1);
  // Spikes on knuckles
  const spikeL1 = scene.add.triangle(-19, 11, 0, 0, -1, -2.5, 1, -2.5, STONE_LT);
  const spikeL2 = scene.add.triangle(-15, 11, 0, 0, -1, -2.5, 1, -2.5, STONE_LT);
  const spikeR1 = scene.add.triangle(15, 11, 0, 0, -1, -2.5, 1, -2.5, STONE_LT);
  const spikeR2 = scene.add.triangle(19, 11, 0, 0, -1, -2.5, 1, -2.5, STONE_LT);

  // Head — chunky blocky
  const head = scene.add.polygon(0, -12, [-7, -4, -8, 4, 8, 4, 7, -4], STONE).setStrokeStyle(0.8, 0x000, 1);
  const headHi = scene.add.rectangle(-3, -13, 4, 1.5, STONE_HI, 0.7);

  // Glowing eye slits (horizontal)
  const eyeGlow = scene.add.rectangle(0, -12, 12, 2, EYE, 0.4);
  const eyeL = scene.add.rectangle(-3, -12, 3, 1.2, EYE);
  const eyeR = scene.add.rectangle(3, -12, 3, 1.2, EYE);
  scene.tweens.add({ targets: [eyeL, eyeR, eyeGlow], alpha: { from: 1, to: 0.55 }, yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });

  // Jaw line
  const jaw = scene.add.line(0, 0, -5, -10, 5, -10, CRACK, 1).setLineWidth(0.6).setOrigin(0, 0);

  // Shoulder rocks (jagged stones jutting up)
  const shoulderL1 = scene.add.triangle(-12, -8, 0, 0, -2.5, -3, 2.5, -3, STONE_DK);
  const shoulderL2 = scene.add.triangle(-9, -9, 0, 0, -2, -2.5, 2, -2.5, STONE_DK);
  const shoulderR1 = scene.add.triangle(12, -8, 0, 0, -2.5, -3, 2.5, -3, STONE_DK);
  const shoulderR2 = scene.add.triangle(9, -9, 0, 0, -2, -2.5, 2, -2.5, STONE_DK);

  // Moss tufts (scattered)
  const moss1 = scene.add.ellipse(-6, -2, 3, 1.4, MOSS, 0.85);
  const moss2 = scene.add.ellipse(8, 3, 3.5, 1.5, MOSS, 0.85);
  const moss3 = scene.add.ellipse(0, -8, 2, 1, MOSS, 0.7);

  c.add([
    shadow,
    legL, legR, legHiL, legHiR, footL, footR,
    torso, torsoHi, crack1, crack2, crack3,
    heartGlow, heart, heartCore,
    armL, armR, armHiL, armHiR,
    fistL, fistR, spikeL1, spikeL2, spikeR1, spikeR2,
    shoulderL1, shoulderL2, shoulderR1, shoulderR2,
    head, headHi,
    eyeGlow, eyeL, eyeR, jaw,
    moss1, moss2, moss3,
  ]);
}

// ---------- BROOD (egg-sack carrier with parasitic embryo) ----------
function buildBrood(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  const SAC_DK = 0x4a5010;
  const SAC = 0x8a9020;
  const SAC_HI = 0xc8d040;
  const SAC_GLOW = 0xe8f070;
  const EMBRYO = 0x301818;
  const EMBRYO_HI = 0x603030;
  const SHELL_DK = 0x2a1a08;
  const SHELL = 0x4a3818;
  const VEIN = 0x7a3010;
  const EYE = 0xff2010;
  const FANG = 0xece0c0;

  const shadow = scene.add.ellipse(0, 14, 22, 4, 0x000000, 0.5);

  // 4 jointed insect legs (front + back, both sides)
  const legFL_u = scene.add.line(0, 0, -8, 4, -13, -2, SHELL_DK, 1).setLineWidth(1.2).setOrigin(0, 0);
  const legFL_l = scene.add.line(0, 0, -13, -2, -16, 8, SHELL_DK, 1).setLineWidth(1).setOrigin(0, 0);
  const legBL_u = scene.add.line(0, 0, -7, 8, -12, 4, SHELL_DK, 1).setLineWidth(1.2).setOrigin(0, 0);
  const legBL_l = scene.add.line(0, 0, -12, 4, -15, 12, SHELL_DK, 1).setLineWidth(1).setOrigin(0, 0);
  const legFR_u = scene.add.line(0, 0, 8, 4, 13, -2, SHELL_DK, 1).setLineWidth(1.2).setOrigin(0, 0);
  const legFR_l = scene.add.line(0, 0, 13, -2, 16, 8, SHELL_DK, 1).setLineWidth(1).setOrigin(0, 0);
  const legBR_u = scene.add.line(0, 0, 7, 8, 12, 4, SHELL_DK, 1).setLineWidth(1.2).setOrigin(0, 0);
  const legBR_l = scene.add.line(0, 0, 12, 4, 15, 12, SHELL_DK, 1).setLineWidth(1).setOrigin(0, 0);
  scene.tweens.add({ targets: [legFL_u, legFL_l, legBR_u, legBR_l], y: "-=1.5", yoyo: true, repeat: -1, duration: 280, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [legFR_u, legFR_l, legBL_u, legBL_l], y: "-=1.5", yoyo: true, repeat: -1, duration: 280, ease: "Sine.easeInOut", delay: 140 });

  // Carrier shell underbelly (insectoid carapace)
  const shell = scene.add.ellipse(0, 7, 17, 9, SHELL).setStrokeStyle(0.7, 0x000, 1);
  const shellHi = scene.add.ellipse(-2, 5, 9, 4, SHELL_DK, 0.7);

  // Pulsing egg sack on top (the bright distinctive feature)
  const sackGlow = scene.add.ellipse(0, -2, 22, 18, SAC_GLOW, 0.35);
  const sack = scene.add.ellipse(0, -2, 18, 15, SAC).setStrokeStyle(0.8, SAC_DK, 1);
  const sackHi = scene.add.ellipse(-2, -4, 8, 5, SAC_HI, 0.85);
  scene.tweens.add({ targets: [sack, sackHi], scaleX: { from: 1, to: 1.08 }, scaleY: { from: 1, to: 0.95 }, yoyo: true, repeat: -1, duration: 760, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: sackGlow, scale: { from: 1, to: 1.18 }, alpha: { from: 0.3, to: 0.55 }, yoyo: true, repeat: -1, duration: 760, ease: "Sine.easeInOut" });

  // Veins on sack
  const vein1 = scene.add.line(0, 0, -6, -6, -2, 2, VEIN, 0.8).setLineWidth(0.6).setOrigin(0, 0);
  const vein2 = scene.add.line(0, 0, 5, -6, 1, 2, VEIN, 0.8).setLineWidth(0.6).setOrigin(0, 0);
  const vein3 = scene.add.line(0, 0, -3, 4, 4, 5, VEIN, 0.7).setLineWidth(0.5).setOrigin(0, 0);

  // Embryo silhouette inside (curled creature)
  const embryoBody = scene.add.ellipse(0, -1, 9, 7, EMBRYO, 0.85);
  const embryoHead = scene.add.circle(-2, -3, 2.8, EMBRYO);
  const embryoEye1 = scene.add.circle(-3, -3.5, 0.5, EYE);
  const embryoEye2 = scene.add.circle(-1, -3.5, 0.5, EYE);
  const embryoTail = scene.add.line(0, 0, 2, 0, 5, 2, EMBRYO_HI, 0.85).setLineWidth(0.8).setOrigin(0, 0);
  // Mini fangs
  const embryoFang1 = scene.add.triangle(-3, -2, 0, 0, -0.4, 1, 0.4, 1, FANG);
  const embryoFang2 = scene.add.triangle(-1, -2, 0, 0, -0.4, 1, 0.4, 1, FANG);
  // Embryo twitch
  scene.tweens.add({ targets: [embryoBody, embryoHead, embryoEye1, embryoEye2, embryoTail, embryoFang1, embryoFang2], x: "+=0.6", y: "+=0.3", yoyo: true, repeat: -1, duration: 540, ease: "Sine.easeInOut" });

  // Carrier head poking out front
  const head = scene.add.circle(0, 9, 4, SHELL_DK).setStrokeStyle(0.5, 0x000, 1);
  const headEye1 = scene.add.circle(-1.3, 9, 0.7, EYE);
  const headEye2 = scene.add.circle(1.3, 9, 0.7, EYE);
  const headEyeShine1 = scene.add.circle(-1, 8.7, 0.25, 0xfff080);
  const headEyeShine2 = scene.add.circle(1.6, 8.7, 0.25, 0xfff080);

  // Mandibles
  const mandL = scene.add.line(0, 0, -2, 11, -4, 13, SHELL_DK, 1).setLineWidth(1).setOrigin(0, 0);
  const mandR = scene.add.line(0, 0, 2, 11, 4, 13, SHELL_DK, 1).setLineWidth(1).setOrigin(0, 0);
  const fangL = scene.add.triangle(-4, 13, 0, 0, -0.5, 1.4, 0.5, 1.4, FANG);
  const fangR = scene.add.triangle(4, 13, 0, 0, -0.5, 1.4, 0.5, 1.4, FANG);

  // Sack-bristles on top
  const bristle1 = scene.add.line(0, 0, -3, -10, -3, -13, 0x301a08, 1).setLineWidth(0.6).setOrigin(0, 0);
  const bristle2 = scene.add.line(0, 0, 0, -11, 0, -14, 0x301a08, 1).setLineWidth(0.6).setOrigin(0, 0);
  const bristle3 = scene.add.line(0, 0, 3, -10, 3, -13, 0x301a08, 1).setLineWidth(0.6).setOrigin(0, 0);

  c.add([
    shadow,
    legFL_u, legFL_l, legBL_u, legBL_l, legFR_u, legFR_l, legBR_u, legBR_l,
    shell, shellHi,
    sackGlow, sack, sackHi,
    vein1, vein2, vein3,
    embryoBody, embryoHead, embryoEye1, embryoEye2, embryoTail, embryoFang1, embryoFang2,
    bristle1, bristle2, bristle3,
    head, headEye1, headEye2, headEyeShine1, headEyeShine2,
    mandL, mandR, fangL, fangR,
  ]);
}

// ---------- SKELETON (rattling bone warrior, sword + buckler) ----------
function buildSkeleton(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // BONE REAVER — a cursed undead warrior. Rune-lit bones, a tattered war
  // banner strapped to its back, a crown of jagged bone shards, floating
  // bone fragments orbiting from the necromantic curse that animates it.
  const BONE = 0xeae6d8;
  const BONE_DK = 0x9a948a;
  const BONE_HI = 0xfdfaf0;
  const RUST = 0x6a5a3a;
  const RUNE = 0x6affc0;
  const CLOTH = 0x4a1020;

  // Necrotic curse halo
  const curse = scene.add.circle(0, -2, 16, RUNE, 0.1);
  scene.tweens.add({ targets: curse, scale: { from: 0.9, to: 1.18 }, alpha: { from: 0.06, to: 0.18 }, yoyo: true, repeat: -1, duration: 950 });

  // ---- TATTERED WAR BANNER on a bone pole behind the body ----
  const pole = scene.add.rectangle(-8.5, -3, 1, 22, 0x4a3a26).setStrokeStyle(0.4, 0x000, 1).setRotation(0.12);
  const banner = scene.add.polygon(-12, -10, [0, -6, 7, -5, 6, 1, 7, 7, 0, 6, 2, 0], CLOTH, 0.92).setStrokeStyle(0.5, 0x2a0810, 1);
  const bannerRune = scene.add.triangle(-10.5, -9, 0, -3, -2, 2, 2, 2, RUNE, 0.85);
  scene.tweens.add({ targets: [banner, bannerRune], scaleX: { from: 1, to: 0.82 }, skewX: 0.1, yoyo: true, repeat: -1, duration: 880, ease: "Sine.easeInOut" });

  // ---- FLOATING ORBITING BONE FRAGMENTS (curse-levitated) ----
  const frags: Phaser.GameObjects.GameObject[] = [];
  for (let i = 0; i < 3; i++) {
    const ang = (i / 3) * Math.PI * 2;
    const fx = Math.cos(ang) * 13;
    const fy = Math.sin(ang) * 9 - 2;
    const frag = scene.add.rectangle(fx, fy, 3, 1, BONE_DK).setStrokeStyle(0.3, 0x000, 1);
    frags.push(frag);
    scene.tweens.add({ targets: frag, angle: 360, repeat: -1, duration: 2600 + i * 500 });
    scene.tweens.add({ targets: frag, y: fy - 3, yoyo: true, repeat: -1, duration: 700 + i * 130, ease: "Sine.easeInOut" });
  }

  // Legs — thin bone shanks with knee joints
  const legL = scene.add.rectangle(-2.5, 11, 1.8, 8, BONE).setStrokeStyle(0.5, BONE_DK, 1);
  const legR = scene.add.rectangle(2.5, 11, 1.8, 8, BONE).setStrokeStyle(0.5, BONE_DK, 1);
  const kneeL = scene.add.circle(-2.5, 9, 1, BONE_DK);
  const kneeR = scene.add.circle(2.5, 9, 1, BONE_DK);
  const footL = scene.add.rectangle(-2.8, 15, 3, 1.2, BONE_DK);
  const footR = scene.add.rectangle(2.8, 15, 3, 1.2, BONE_DK);
  // Pelvis + ribcage (rune-lit core)
  const pelvis = scene.add.ellipse(0, 6, 8, 4, BONE).setStrokeStyle(0.5, BONE_DK, 1);
  const spine = scene.add.rectangle(0, 1, 1.6, 9, BONE);
  const coreGlow = scene.add.circle(0, 1, 2.6, RUNE, 0.55);
  scene.tweens.add({ targets: coreGlow, scale: { from: 0.7, to: 1.3 }, alpha: { from: 0.3, to: 0.7 }, yoyo: true, repeat: -1, duration: 620 });
  const rib1 = scene.add.ellipse(0, -1, 9, 5, 0x000, 0).setStrokeStyle(1.2, BONE, 0.95);
  const rib2 = scene.add.ellipse(0, 1.5, 8, 4.5, 0x000, 0).setStrokeStyle(1.1, BONE, 0.9);
  const rib3 = scene.add.ellipse(0, 4, 6.5, 3.5, 0x000, 0).setStrokeStyle(1, BONE, 0.85);
  // Shoulders + arms
  const clavicle = scene.add.rectangle(0, -3.5, 9, 1.2, BONE).setStrokeStyle(0.3, BONE_DK, 1);
  const armL = scene.add.rectangle(-6, 0, 1.6, 9, BONE).setStrokeStyle(0.5, BONE_DK, 1).setRotation(0.2);
  const armR = scene.add.rectangle(6, 1, 1.6, 8, BONE).setStrokeStyle(0.5, BONE_DK, 1).setRotation(-0.3);
  // Rune-etched rusty broadsword raised in right hand
  const sword = scene.add.rectangle(8.5, -5, 2, 14, 0xc8c0b0).setStrokeStyle(0.5, RUST, 1).setRotation(-0.35);
  const swordRune = scene.add.line(0, 0, 8.5, -1, 8.5, -10, RUNE, 0.8).setLineWidth(0.6).setOrigin(0, 0).setRotation(-0.35);
  const swordTip = scene.add.triangle(10.7, -11.5, 0, 3, -1.3, -3, 1.3, -3, 0xd8d0c0).setRotation(-0.35);
  const guard = scene.add.rectangle(7.5, 0.5, 4.5, 1.4, RUST).setRotation(-0.35);
  scene.tweens.add({ targets: swordRune, alpha: { from: 0.4, to: 0.9 }, yoyo: true, repeat: -1, duration: 520 });
  // Round buckler shield in left hand (rune boss)
  const buckler = scene.add.circle(-7, 2, 4, 0x5a4a2a).setStrokeStyle(1, 0x2a1a08, 1);
  const bucklerRing = scene.add.circle(-7, 2, 4, 0x000, 0).setStrokeStyle(0.5, BONE_DK, 0.8);
  const bucklerBoss = scene.add.circle(-7, 2, 1.5, RUNE, 0.9);
  // Skull with cracked dome + crown of bone shards
  const skull = scene.add.circle(0, -8, 5, BONE).setStrokeStyle(0.6, BONE_DK, 1);
  const skullCrack = scene.add.line(0, 0, -1.5, -12, 1, -7, BONE_DK, 0.9).setLineWidth(0.5).setOrigin(0, 0);
  const crownL = scene.add.triangle(-3.5, -12.5, 0, -3, -1.4, 2, 1.4, 2, BONE).setStrokeStyle(0.3, BONE_DK, 1);
  const crownM = scene.add.triangle(0, -13.5, 0, -3.5, -1.4, 2, 1.4, 2, BONE_HI).setStrokeStyle(0.3, BONE_DK, 1);
  const crownR = scene.add.triangle(3.5, -12.5, 0, -3, -1.4, 2, 1.4, 2, BONE).setStrokeStyle(0.3, BONE_DK, 1);
  const jaw = scene.add.rectangle(0, -4.5, 5, 2, BONE).setStrokeStyle(0.4, BONE_DK, 1);
  const teeth1 = scene.add.rectangle(-1.3, -4.5, 0.5, 1.4, 0x000);
  const teeth2 = scene.add.rectangle(0, -4.5, 0.5, 1.4, 0x000);
  const teeth3 = scene.add.rectangle(1.3, -4.5, 0.5, 1.4, 0x000);
  const socketL = scene.add.circle(-1.8, -8.5, 1.5, 0x0a1410);
  const socketR = scene.add.circle(1.8, -8.5, 1.5, 0x0a1410);
  const emberL = scene.add.circle(-1.8, -8.5, 0.8, RUNE, 0.95);
  const emberR = scene.add.circle(1.8, -8.5, 0.8, RUNE, 0.95);
  scene.tweens.add({ targets: [emberL, emberR], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 400 });
  // Sword raise/lower menace
  scene.tweens.add({ targets: [sword, swordRune, swordTip, guard], rotation: { from: -0.35, to: -0.6 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

  c.add([curse, pole, banner, bannerRune, ...frags,
    legL, legR, kneeL, kneeR, footL, footR, pelvis, spine, coreGlow,
    clavicle, armL, armR, buckler, bucklerRing, bucklerBoss,
    rib1, rib2, rib3, guard, sword, swordRune, swordTip,
    skull, skullCrack, crownL, crownM, crownR,
    jaw, teeth1, teeth2, teeth3, socketL, socketR, emberL, emberR]);
}

// ---------- DEMON (winged red brute, horns, glowing maw) ----------
function buildDemon(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // BRIMSTONE FIEND — a winged hell-brute wreathed in sulfur fire. Torn
  // membrane wings, a barbed arrow-tipped tail, a burning sigil branded into
  // its chest, and embers rising off its molten hide.
  const SKIN = 0xc0281a;
  const SKIN_DK = 0x7a1408;
  const SKIN_HI = 0xff5a3a;
  const HORN = 0x2a1a14;
  const FIRE = 0xff8a00;
  const FIRE_HI = 0xffd84a;

  // Sulfur fire aura
  const aura = scene.add.circle(0, 2, 19, FIRE, 0.12);
  scene.tweens.add({ targets: aura, scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.08, to: 0.2 }, yoyo: true, repeat: -1, duration: 720 });

  // ---- BARBED TAIL curling behind, arrow tip ----
  const tail = scene.add.line(0, 0, 2, 10, -7, 6, SKIN_DK, 1).setLineWidth(2.2).setOrigin(0, 0);
  const tail2 = scene.add.line(0, 0, -7, 6, -11, 11, SKIN_DK, 1).setLineWidth(1.8).setOrigin(0, 0);
  const tailBarb = scene.add.triangle(-11, 11, 0, 4, -3, -3, 3, -3, HORN).setStrokeStyle(0.4, 0x000, 1).setRotation(-0.7);
  scene.tweens.add({ targets: [tail2, tailBarb], angle: { from: -6, to: 6 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

  // ---- TORN MEMBRANE WINGS with bone struts ----
  const wingL = scene.add.polygon(-8, -3, [0, -9, -11, -6, -14, 2, -8, 1, -10, 9, -3, 5, -6, 12, -1, 6, 0, 9], SKIN_DK, 0.95).setStrokeStyle(0.8, 0x000, 0.8);
  const wingR = scene.add.polygon(8, -3, [0, -9, 11, -6, 14, 2, 8, 1, 10, 9, 3, 5, 6, 12, 1, 6, 0, 9], SKIN_DK, 0.95).setStrokeStyle(0.8, 0x000, 0.8);
  const wingMemL = scene.add.polygon(-8, -3, [0, -8, -9, -4, -8, 4, 0, 6], 0x9a1808, 0.45);
  const wingMemR = scene.add.polygon(8, -3, [0, -8, 9, -4, 8, 4, 0, 6], 0x9a1808, 0.45);
  const strutL = scene.add.line(0, 0, -8, -3, -14, 2, SKIN_HI, 0.6).setLineWidth(0.7).setOrigin(0, 0);
  const strutR = scene.add.line(0, 0, 8, -3, 14, 2, SKIN_HI, 0.6).setLineWidth(0.7).setOrigin(0, 0);
  scene.tweens.add({ targets: [wingL, wingMemL], scaleX: { from: 1, to: 0.78 }, yoyo: true, repeat: -1, duration: 480, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [wingR, wingMemR], scaleX: { from: 1, to: 0.78 }, yoyo: true, repeat: -1, duration: 480, ease: "Sine.easeInOut" });

  // Hooved legs with claw toes
  const legL = scene.add.rectangle(-3.5, 12, 3.5, 7, SKIN_DK).setStrokeStyle(0.6, 0x000, 1);
  const legR = scene.add.rectangle(3.5, 12, 3.5, 7, SKIN_DK).setStrokeStyle(0.6, 0x000, 1);
  const hoofL = scene.add.triangle(-3.5, 16.5, 0, 3, -3, -2, 3, -2, HORN).setStrokeStyle(0.4, 0x000, 1);
  const hoofR = scene.add.triangle(3.5, 16.5, 0, 3, -3, -2, 3, -2, HORN).setStrokeStyle(0.4, 0x000, 1);
  // Muscular torso with branded sigil
  const torso = scene.add.ellipse(0, 4, 15, 14, SKIN).setStrokeStyle(1, SKIN_DK, 1);
  const abs = scene.add.line(0, 0, 0, -1, 0, 9, SKIN_DK, 0.6).setLineWidth(1).setOrigin(0, 0);
  const pecHi = scene.add.ellipse(-3, 0, 5, 4, SKIN_HI, 0.4);
  const sigilGlow = scene.add.circle(0, 4, 4, FIRE, 0.4);
  const sigil = scene.add.polygon(0, 4, [0, -3, 2.6, 1.5, -2.6, 1.5], FIRE_HI, 0).setStrokeStyle(0.8, FIRE_HI, 0.9);
  const sigilBar = scene.add.line(0, 0, -2, 4, 2, 4, FIRE_HI, 0.9).setLineWidth(0.7).setOrigin(0, 0);
  scene.tweens.add({ targets: [sigilGlow, sigil, sigilBar], alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 580 });
  // Arms with clawed fists
  const armL = scene.add.rectangle(-8, 3, 3.5, 10, SKIN).setStrokeStyle(0.6, SKIN_DK, 1).setRotation(0.25);
  const armR = scene.add.rectangle(8, 3, 3.5, 10, SKIN).setStrokeStyle(0.6, SKIN_DK, 1).setRotation(-0.25);
  const fistL = scene.add.circle(-9.5, 8, 2.6, SKIN_DK);
  const fistR = scene.add.circle(9.5, 8, 2.6, SKIN_DK);
  const clawL1 = scene.add.triangle(-11, 9.5, 0, 2.5, -0.8, -1, 0.8, -1, 0xe8d0b0);
  const clawL2 = scene.add.triangle(-9, 10, 0, 2.5, -0.8, -1, 0.8, -1, 0xe8d0b0);
  const clawR1 = scene.add.triangle(11, 9.5, 0, 2.5, -0.8, -1, 0.8, -1, 0xe8d0b0);
  const clawR2 = scene.add.triangle(9, 10, 0, 2.5, -0.8, -1, 0.8, -1, 0xe8d0b0);
  // Head with great curved horns
  const head = scene.add.ellipse(0, -8, 11, 10, SKIN).setStrokeStyle(0.8, SKIN_DK, 1);
  const hornL = scene.add.ellipse(-5, -13, 2.4, 7, HORN).setStrokeStyle(0.5, 0x000, 1).setRotation(-0.5);
  const hornR = scene.add.ellipse(5, -13, 2.4, 7, HORN).setStrokeStyle(0.5, 0x000, 1).setRotation(0.5);
  const browL = scene.add.triangle(-3, -11, 0, 2, -3, -1, 1, -1, SKIN_DK);
  const browR = scene.add.triangle(3, -11, 0, 2, 3, -1, -1, -1, SKIN_DK);
  const eyeL = scene.add.circle(-2.6, -9, 1.6, FIRE_HI, 1);
  const eyeR = scene.add.circle(2.6, -9, 1.6, FIRE_HI, 1);
  const pupL = scene.add.rectangle(-2.6, -9, 0.7, 2.2, 0x000);
  const pupR = scene.add.rectangle(2.6, -9, 0.7, 2.2, 0x000);
  // Glowing maw with drool
  const maw = scene.add.ellipse(0, -5, 5, 2.4, FIRE, 0.95).setStrokeStyle(0.4, 0x000, 1);
  const fangDL = scene.add.triangle(-1.6, -5, 0, 1.6, -0.5, -0.8, 0.5, -0.8, 0xe8d0b0);
  const fangDR = scene.add.triangle(1.6, -5, 0, 1.6, -0.5, -0.8, 0.5, -0.8, 0xe8d0b0);
  scene.tweens.add({ targets: maw, alpha: { from: 0.6, to: 1 }, scaleX: { from: 0.9, to: 1.1 }, yoyo: true, repeat: -1, duration: 500 });
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 600 });
  // Rising embers
  for (let i = 0; i < 3; i++) {
    const ex = -5 + i * 5;
    const ember = scene.add.circle(ex, 6, 0.9, FIRE_HI, 0.9);
    c.add(ember);
    scene.tweens.add({ targets: ember, y: -12, alpha: { from: 0.9, to: 0 }, repeat: -1, duration: 850 + i * 180, delay: i * 220 });
  }

  c.add([aura, tail, tail2, tailBarb, wingL, wingR, wingMemL, wingMemR, strutL, strutR,
    legL, legR, hoofL, hoofR, torso, abs, pecHi, sigilGlow, sigil, sigilBar,
    armL, armR, fistL, fistR, clawL1, clawL2, clawR1, clawR2,
    head, hornL, hornR, browL, browR, maw, fangDL, fangDR, eyeL, eyeR, pupL, pupR]);
}

// ---------- SPIDER (scuttling 8-legged horror, fangs, abdomen) ----------
function buildSpider(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // VENOM BROODMOTHER — a bloated arachnid horror. 8 bristled jointed legs,
  // a pulsing toxic abdomen marked with a glowing hourglass, a cluster of
  // eight eyes, dripping venom fangs, and silk strands trailing from its spinneret.
  const BODY = 0x2a1442;
  const BODY_HI = 0x5a2a8a;
  const LEG = 0x1a0a2a;
  const VENOM = 0x8aff3a;
  const TOXIC = 0xb6ff6a;

  // ---- TRAILING SILK STRANDS behind the abdomen ----
  const silk1 = scene.add.line(0, 0, -2, 11, -10, 16, 0xd8d0e8, 0.4).setLineWidth(0.6).setOrigin(0, 0);
  const silk2 = scene.add.line(0, 0, 2, 11, 9, 17, 0xd8d0e8, 0.35).setLineWidth(0.5).setOrigin(0, 0);
  scene.tweens.add({ targets: [silk1, silk2], alpha: { from: 0.4, to: 0.1 }, yoyo: true, repeat: -1, duration: 600 });

  // 8 legs (4 each side), jointed, with bristles + foot claws
  const legs: Phaser.GameObjects.GameObject[] = [];
  for (let side = -1; side <= 1; side += 2) {
    for (let i = 0; i < 4; i++) {
      const lx = side * 4;
      const midX = lx + side * 7;
      const midY = -3 + i * 2;
      const tipX = lx + side * 11.5;
      const tipY = 5 + i * 2;
      const upper = scene.add.line(0, 0, lx, 2 + i * 1.5, midX, midY, LEG, 1).setLineWidth(1.7).setOrigin(0, 0);
      const lower = scene.add.line(0, 0, midX, midY, tipX, tipY, LEG, 1).setLineWidth(1.3).setOrigin(0, 0);
      const claw = scene.add.circle(tipX, tipY, 0.9, 0x000);
      const bristle = scene.add.line(0, 0, midX, midY, midX + side * 1.5, midY - 2, LEG, 0.8).setLineWidth(0.5).setOrigin(0, 0);
      legs.push(upper, lower, claw, bristle);
      scene.tweens.add({ targets: [upper, lower, claw, bristle], rotation: { from: -0.13 * side, to: 0.13 * side }, yoyo: true, repeat: -1, duration: 170 + i * 28, ease: "Sine.easeInOut" });
    }
  }
  // Abdomen (big rear bulb) — pulsing toxic glow
  const abGlow = scene.add.ellipse(0, 5, 20, 18, VENOM, 0.12);
  const abdomen = scene.add.ellipse(0, 5, 16, 14, BODY).setStrokeStyle(1, 0x000, 1);
  const abdomenMark = scene.add.ellipse(0, 5, 7, 9, BODY_HI, 0.6);
  // Venom-glowing hourglass
  const hourglass = scene.add.triangle(0, 5, 0, 0, -3, -4, 3, -4, VENOM, 0.95);
  const hourglass2 = scene.add.triangle(0, 5, 0, 0, -3, 4, 3, 4, VENOM, 0.95);
  // Mottled spots on abdomen
  const spot1 = scene.add.circle(-4, 2, 1.4, 0x140828, 0.7);
  const spot2 = scene.add.circle(5, 7, 1.6, 0x140828, 0.7);
  const spot3 = scene.add.circle(-3, 9, 1.2, 0x140828, 0.6);
  scene.tweens.add({ targets: [abGlow, hourglass, hourglass2], alpha: { from: 0.45, to: 0.95 }, scale: { from: 0.95, to: 1.08 }, yoyo: true, repeat: -1, duration: 700 });
  // Cephalothorax (front)
  const head = scene.add.ellipse(0, -6, 11, 9, BODY).setStrokeStyle(1, 0x000, 1);
  const headHi = scene.add.ellipse(-2, -7, 5, 3.5, BODY_HI, 0.5);
  // Cluster of 8 glowing eyes
  const eyes: Phaser.GameObjects.GameObject[] = [];
  const eyePos = [[-2.5, -8], [2.5, -8], [-1.2, -9.5], [1.2, -9.5], [-3.5, -6], [3.5, -6], [-0.8, -7], [0.8, -7]];
  for (const [ex, ey] of eyePos) {
    eyes.push(scene.add.circle(ex, ey, 1.0, 0xff3a3a, 1));
  }
  scene.tweens.add({ targets: eyes, alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 450 });
  // Pedipalps + dripping venom fangs
  const palpL = scene.add.line(0, 0, -3, -3, -5, 0, LEG, 0.9).setLineWidth(1).setOrigin(0, 0);
  const palpR = scene.add.line(0, 0, 3, -3, 5, 0, LEG, 0.9).setLineWidth(1).setOrigin(0, 0);
  const fangL = scene.add.triangle(-2, -2, 0, 4, -1.5, -2, 1, -2, 0xeae0d0).setStrokeStyle(0.4, 0x000, 1);
  const fangR = scene.add.triangle(2, -2, 0, 4, 1.5, -2, -1, -2, 0xeae0d0).setStrokeStyle(0.4, 0x000, 1);
  const venomDrip = scene.add.circle(0, 2, 0.9, TOXIC, 0.95);
  scene.tweens.add({ targets: venomDrip, y: 7, alpha: { from: 0.95, to: 0 }, repeat: -1, duration: 900 });

  c.add([silk1, silk2, ...legs, abGlow, abdomen, abdomenMark, spot1, spot2, spot3,
    hourglass, hourglass2, head, headHi, ...eyes, palpL, palpR, fangL, fangR, venomDrip]);
}

// ---------- CRYSTAL (slow living gem golem, refracting shards) ----------
function buildCrystal(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // PRISM GOLEM — a living gemstone construct. A great faceted core sits on
  // stubby crystal legs, jagged shard arms jut from its sides, prismatic light
  // rays fan out, and orbiting shards refract a pulsing inner light.
  const GEM = 0x3ac0e0;
  const GEM_DK = 0x1a6a9a;
  const GEM_HI = 0xa6f0ff;
  const PRISM_A = 0xff8ad8;
  const PRISM_B = 0xffe88a;

  // Glow halo
  const aura = scene.add.circle(0, 2, 22, GEM, 0.18);
  scene.tweens.add({ targets: aura, scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.1, to: 0.28 }, yoyo: true, repeat: -1, duration: 1100 });

  // ---- PRISMATIC LIGHT RAYS fanning out (refraction) ----
  const rays: Phaser.GameObjects.GameObject[] = [];
  const rayCols = [PRISM_A, GEM_HI, PRISM_B, GEM_HI, PRISM_A, PRISM_B];
  for (let i = 0; i < 6; i++) {
    const ang = (i / 6) * Math.PI * 2 + 0.4;
    const ray = scene.add.line(0, 0, 0, 2, Math.cos(ang) * 20, Math.sin(ang) * 20 + 2, rayCols[i], 0.35).setLineWidth(1.4).setOrigin(0, 0);
    rays.push(ray);
    scene.tweens.add({ targets: ray, alpha: { from: 0.1, to: 0.45 }, yoyo: true, repeat: -1, duration: 800 + i * 120 });
  }

  // ---- STUBBY CRYSTAL LEGS ----
  const legL = scene.add.polygon(-5, 13, [0, -4, 2.5, 3, -2.5, 3], GEM_DK, 0.9).setStrokeStyle(0.6, 0x0a3a5a, 1);
  const legR = scene.add.polygon(5, 13, [0, -4, 2.5, 3, -2.5, 3], GEM_DK, 0.9).setStrokeStyle(0.6, 0x0a3a5a, 1);

  // ---- SHARD ARMS jutting from sides ----
  const armL = scene.add.polygon(-11, 3, [0, -2, 4, 1, 0, 6, -3, 2], GEM, 0.9).setStrokeStyle(0.6, GEM_DK, 1).setRotation(-0.4);
  const armR = scene.add.polygon(11, 3, [0, -2, -4, 1, 0, 6, 3, 2], GEM, 0.9).setStrokeStyle(0.6, GEM_DK, 1).setRotation(0.4);
  const armTipL = scene.add.triangle(-14, 6, 0, 3, -2, -2, 2, -2, GEM_HI, 0.85);
  const armTipR = scene.add.triangle(14, 6, 0, 3, -2, -2, 2, -2, GEM_HI, 0.85);

  // Core body — big faceted gem (hexagonal)
  const body = scene.add.polygon(0, 2, [0, -14, 9, -7, 9, 7, 0, 14, -9, 7, -9, -7], GEM, 0.92).setStrokeStyle(1.2, GEM_DK, 1);
  // Facet highlights
  const facetL = scene.add.polygon(0, 2, [0, -14, -9, -7, -9, 7, 0, 0], GEM_DK, 0.5);
  const facetR = scene.add.polygon(0, 2, [0, -14, 9, -7, 6, 0, 0, -2], GEM_HI, 0.55);
  const facetLow = scene.add.polygon(0, 2, [0, 14, -9, 7, 0, 2, 9, 7], 0x0a4a6a, 0.4);
  const shine = scene.add.line(0, 0, -3, -8, 2, -2, GEM_HI, 0.9).setLineWidth(1.4).setOrigin(0, 0);
  const shine2 = scene.add.line(0, 0, 3, -10, 5, -4, GEM_HI, 0.7).setLineWidth(1).setOrigin(0, 0);
  // Glowing eye-facets
  const eyeL = scene.add.polygon(-3, -2, [0, -2, 1.5, 1, -1.5, 1], PRISM_A, 0.9);
  const eyeR = scene.add.polygon(3, -2, [0, -2, 1.5, 1, -1.5, 1], PRISM_A, 0.9);
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 520 });
  // Orbiting shard satellites
  const shards: Phaser.GameObjects.GameObject[] = [];
  for (let i = 0; i < 5; i++) {
    const ang = (i / 5) * Math.PI * 2;
    const sx = Math.cos(ang) * 15;
    const sy = Math.sin(ang) * 12 + 2;
    const col = i % 2 === 0 ? GEM_HI : PRISM_B;
    const shard = scene.add.triangle(sx, sy, 0, -4, -2.5, 3, 2.5, 3, col, 0.85).setStrokeStyle(0.5, GEM_DK, 1);
    shards.push(shard);
    scene.tweens.add({ targets: shard, angle: 360, repeat: -1, duration: 3000 + i * 380 });
    scene.tweens.add({ targets: shard, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 700 + i * 110 });
  }
  // Glowing inner core
  const core = scene.add.circle(0, 2, 3.4, GEM_HI, 0.9);
  const coreFlash = scene.add.circle(0, 2, 1.6, 0xffffff, 0.9);
  scene.tweens.add({ targets: core, scale: { from: 0.7, to: 1.35 }, alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 600 });
  scene.tweens.add({ targets: coreFlash, scale: { from: 0.5, to: 1.6 }, alpha: { from: 0.9, to: 0.2 }, yoyo: true, repeat: -1, duration: 600 });

  c.add([aura, ...rays, ...shards, legL, legR, armL, armR, armTipL, armTipR,
    body, facetL, facetR, facetLow, eyeL, eyeR, core, coreFlash, shine, shine2]);
}

// ---------- VAMPIRE (caped noble, pale, fanged, blood-red eyes) ----------
function buildVampire(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // BLOOD COUNT — an aristocratic nightstalker. A bat-winged cape spreads
  // behind him, blood motes drift up from his grasp, his eyes pulse with a
  // hypnotic crimson glow, and a cold mist pools at his feet.
  const CAPE = 0x1a0510;
  const CAPE_IN = 0x6a0020;
  const SKIN = 0xd8d0d4;
  const SKIN_DK = 0x9a8a94;
  const BLOOD = 0xc8102a;

  // Cold ground mist
  const mist = scene.add.ellipse(0, 13, 26, 6, 0x6a4a6a, 0.22);
  scene.tweens.add({ targets: mist, scaleX: { from: 0.9, to: 1.2 }, alpha: { from: 0.12, to: 0.28 }, yoyo: true, repeat: -1, duration: 1300 });

  // ---- BAT-WINGED CAPE (scalloped membrane edge) ----
  const cape = scene.add.polygon(0, 4, [0, -12, -14, -2, -12, 4, -14, 8, -10, 7, -11, 13, -6, 10, -7, 15, 0, 11, 7, 15, 6, 10, 11, 13, 10, 7, 14, 8, 12, 4, 14, -2], CAPE, 0.97).setStrokeStyle(0.8, 0x000, 0.8);
  const capeInL = scene.add.triangle(-5, 6, 0, -8, -5, 8, 3, 6, CAPE_IN, 0.6);
  const capeInR = scene.add.triangle(5, 6, 0, -8, 5, 8, -3, 6, CAPE_IN, 0.6);
  const wingRibL = scene.add.line(0, 0, 0, -8, -12, 6, 0x3a0818, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  const wingRibR = scene.add.line(0, 0, 0, -8, 12, 6, 0x3a0818, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  scene.tweens.add({ targets: [cape, capeInL, capeInR, wingRibL, wingRibR], scaleX: { from: 1, to: 1.1 }, yoyo: true, repeat: -1, duration: 880, ease: "Sine.easeInOut" });
  // Body — formal dark suit with tails
  const torso = scene.add.rectangle(0, 4, 9, 13, 0x14101a).setStrokeStyle(0.6, 0x000, 1);
  const lapelL = scene.add.triangle(-2.5, 2, 0, -5, -2, 5, 1, 0, 0x2a1020);
  const lapelR = scene.add.triangle(2.5, 2, 0, -5, 2, 5, -1, 0, 0x2a1020);
  const shirt = scene.add.triangle(0, 2, 0, 6, -2.5, -4, 2.5, -4, 0xd0c8cc);
  const medallion = scene.add.circle(0, 3, 1.5, 0xc8a02a).setStrokeStyle(0.4, 0x6a4a00, 1);
  const medGem = scene.add.circle(0, 3, 0.7, BLOOD);
  // Raised clawed hand offering, blood motes rising
  const handR = scene.add.circle(8, 0, 1.6, SKIN).setStrokeStyle(0.4, SKIN_DK, 1).setRotation(-0.3);
  const fingerR = scene.add.line(0, 0, 8, -1, 9.5, -3, SKIN_DK, 0.9).setLineWidth(0.6).setOrigin(0, 0);
  for (let i = 0; i < 3; i++) {
    const bx = 7 + i * 0.8;
    const mote = scene.add.circle(bx, -2, 0.8, BLOOD, 0.9);
    c.add(mote);
    scene.tweens.add({ targets: mote, y: -10, alpha: { from: 0.9, to: 0 }, repeat: -1, duration: 1000 + i * 200, delay: i * 300 });
  }
  // High collar
  const collarL = scene.add.triangle(-3, -4, 0, -7, -3, 2, 2, 0, CAPE).setStrokeStyle(0.5, CAPE_IN, 1);
  const collarR = scene.add.triangle(3, -4, 0, -7, 3, 2, -2, 0, CAPE).setStrokeStyle(0.5, CAPE_IN, 1);
  // Pale head, slicked hair
  const head = scene.add.circle(0, -9, 5, SKIN).setStrokeStyle(0.6, SKIN_DK, 1);
  const hair = scene.add.polygon(0, -12, [-5, 2, -5, -3, 0, -5, 5, -3, 5, 2, 0, -1], 0x0a0608);
  const widow = scene.add.triangle(0, -10, 0, 2, -1.5, -2, 1.5, -2, 0x0a0608);
  const cheek = scene.add.ellipse(-2.5, -8, 2, 2.5, SKIN_DK, 0.3);
  // Blood-red hypnotic eyes with glow rings
  const eyeGlowL = scene.add.circle(-1.8, -9, 2, BLOOD, 0.35);
  const eyeGlowR = scene.add.circle(1.8, -9, 2, BLOOD, 0.35);
  const eyeL = scene.add.circle(-1.8, -9, 1.2, 0xff0a2a, 1);
  const eyeR = scene.add.circle(1.8, -9, 1.2, 0xff0a2a, 1);
  scene.tweens.add({ targets: [eyeL, eyeR, eyeGlowL, eyeGlowR], alpha: { from: 0.5, to: 1 }, scale: { from: 0.85, to: 1.15 }, yoyo: true, repeat: -1, duration: 500 });
  // Fanged grin with blood drip
  const mouth = scene.add.rectangle(0, -6, 3, 0.8, 0x4a0010);
  const fangL = scene.add.triangle(-1, -5.5, 0, 1.5, -0.6, -1, 0.6, -1, 0xffffff);
  const fangR = scene.add.triangle(1, -5.5, 0, 1.5, -0.6, -1, 0.6, -1, 0xffffff);
  const drip = scene.add.circle(1, -4.5, 0.6, BLOOD, 0.95);
  scene.tweens.add({ targets: drip, y: -1, alpha: { from: 0.95, to: 0 }, repeat: -1, duration: 1100 });

  c.add([mist, cape, capeInL, capeInR, wingRibL, wingRibR,
    torso, lapelL, lapelR, shirt, medallion, medGem, collarL, collarR,
    handR, fingerR, head, hair, widow, cheek,
    eyeGlowL, eyeGlowR, eyeL, eyeR, mouth, fangL, fangR, drip]);
}

// ---------- MAGMA KNIGHT (molten armored juggernaut, cracked glowing plates) ----------
function buildMagmaKnight(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // MOLTEN JUGGERNAUT — an armored colossus forged in a volcano. Cracked
  // obsidian plate bleeds lava, a flaming greatsword rests on one shoulder,
  // a tattered molten cape smolders behind it, and an ember storm rises in its heat.
  const ROCK = 0x2a1a14;
  const ROCK_HI = 0x4a2a1a;
  const LAVA = 0xff6a00;
  const LAVA_HI = 0xffd02a;

  // Heat glow
  const aura = scene.add.circle(0, 4, 24, LAVA, 0.16);
  scene.tweens.add({ targets: aura, scale: { from: 0.92, to: 1.18 }, alpha: { from: 0.1, to: 0.26 }, yoyo: true, repeat: -1, duration: 700 });

  // ---- SMOLDERING MOLTEN CAPE behind ----
  const cape = scene.add.polygon(0, 4, [0, -8, -11, -2, -9, 6, -11, 13, -6, 10, -3, 15, 0, 11, 3, 15, 6, 10, 11, 13, 9, 6, 11, -2], 0x1a0c08, 0.92).setStrokeStyle(0.6, 0x000, 0.8);
  const capeGlow1 = scene.add.line(0, 0, -8, 4, -7, 13, LAVA, 0.7).setLineWidth(0.8).setOrigin(0, 0);
  const capeGlow2 = scene.add.line(0, 0, 8, 4, 7, 13, LAVA, 0.7).setLineWidth(0.8).setOrigin(0, 0);
  scene.tweens.add({ targets: [cape, capeGlow1, capeGlow2], scaleX: { from: 1, to: 1.08 }, yoyo: true, repeat: -1, duration: 950, ease: "Sine.easeInOut" });

  // ---- FLAMING GREATSWORD resting on right shoulder ----
  const swordBlade = scene.add.rectangle(11, -6, 3.5, 22, 0x3a3a3a).setStrokeStyle(0.6, 0x000, 1).setRotation(0.35);
  const swordCore = scene.add.line(0, 0, 11, -14, 11, 4, LAVA, 0.9).setLineWidth(1).setOrigin(0, 0).setRotation(0.35);
  const swordTip = scene.add.triangle(15, -15, 0, 4, -1.8, -4, 1.8, -4, 0x3a3a3a).setRotation(0.35);
  const swordGuard = scene.add.rectangle(7, 3, 6, 1.6, ROCK_HI).setRotation(0.35);
  const swordFlame: Phaser.GameObjects.GameObject[] = [];
  for (let i = 0; i < 3; i++) {
    const fl = scene.add.triangle(9 + i * 2, -8 + i * 4, 0, 0, -1.5, -4, 1.5, -4, i % 2 ? LAVA_HI : LAVA, 0.8).setRotation(0.35);
    swordFlame.push(fl);
    scene.tweens.add({ targets: fl, scaleY: { from: 1, to: 1.5 }, alpha: { from: 0.8, to: 0.4 }, yoyo: true, repeat: -1, duration: 280 + i * 60 });
  }
  scene.tweens.add({ targets: swordCore, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 460 });

  // Heavy legs with lava knee-vents
  const legL = scene.add.rectangle(-4, 13, 5, 8, ROCK).setStrokeStyle(0.8, 0x000, 1);
  const legR = scene.add.rectangle(4, 13, 5, 8, ROCK).setStrokeStyle(0.8, 0x000, 1);
  const lavaLegL = scene.add.line(0, 0, -4, 10, -4, 16, LAVA, 0.9).setLineWidth(1).setOrigin(0, 0);
  const lavaLegR = scene.add.line(0, 0, 4, 10, 4, 16, LAVA, 0.9).setLineWidth(1).setOrigin(0, 0);
  const footL = scene.add.rectangle(-4.5, 17, 6, 2.4, ROCK_HI).setStrokeStyle(0.5, 0x000, 1);
  const footR = scene.add.rectangle(4.5, 17, 6, 2.4, ROCK_HI).setStrokeStyle(0.5, 0x000, 1);
  // Massive armored torso (rock plates)
  const torso = scene.add.rectangle(0, 3, 18, 16, ROCK).setStrokeStyle(1, 0x000, 1);
  const torsoHi = scene.add.rectangle(-4, 0, 5, 12, ROCK_HI, 0.5);
  // Glowing lava cracks across the torso + a molten core vent
  const coreVent = scene.add.circle(0, 4, 3, LAVA_HI, 0.85);
  const coreVentGlow = scene.add.circle(0, 4, 5, LAVA, 0.4);
  const crack1 = scene.add.line(0, 0, -6, -2, 2, 2, LAVA, 0.95).setLineWidth(1.4).setOrigin(0, 0);
  const crack2 = scene.add.line(0, 0, 5, -5, -1, 6, LAVA_HI, 0.9).setLineWidth(1).setOrigin(0, 0);
  const crack3 = scene.add.line(0, 0, -3, 6, 6, 4, LAVA, 0.85).setLineWidth(1.2).setOrigin(0, 0);
  scene.tweens.add({ targets: [crack1, crack2, crack3, coreVent, coreVentGlow], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 550 });
  // Lava drip from the torso
  const lavaDrip = scene.add.circle(3, 9, 0.9, LAVA_HI, 0.95);
  scene.tweens.add({ targets: lavaDrip, y: 15, alpha: { from: 0.95, to: 0 }, repeat: -1, duration: 1000 });
  // Pauldrons with vent spikes
  const pauldronL = scene.add.ellipse(-9, -2, 6, 7, ROCK).setStrokeStyle(0.8, 0x000, 1);
  const pauldronR = scene.add.ellipse(9, -2, 6, 7, ROCK).setStrokeStyle(0.8, 0x000, 1);
  const pauldronCrackL = scene.add.line(0, 0, -10, -3, -8, 1, LAVA, 0.8).setLineWidth(0.7).setOrigin(0, 0);
  const spikeL = scene.add.triangle(-9, -7, 0, 4, -2, -3, 2, -3, ROCK_HI);
  const spikeR = scene.add.triangle(9, -7, 0, 4, -2, -3, 2, -3, ROCK_HI);
  // Helmet — horned visor with burning eye slit
  const helm = scene.add.rectangle(0, -9, 11, 9, ROCK).setStrokeStyle(0.8, 0x000, 1);
  const hornL = scene.add.triangle(-5, -13, 0, 4, -3, -3, 1, 0, ROCK_HI);
  const hornR = scene.add.triangle(5, -13, 0, 4, 3, -3, -1, 0, ROCK_HI);
  const helmCrack = scene.add.line(0, 0, -3, -12, 2, -6, LAVA, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  const visor = scene.add.rectangle(0, -8, 8, 1.8, LAVA_HI, 1);
  const visorGlow = scene.add.rectangle(0, -8, 9, 3, LAVA, 0.4);
  scene.tweens.add({ targets: [visor, visorGlow], alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 480 });
  // Rising ember storm
  for (let i = 0; i < 5; i++) {
    const ex = -7 + i * 3.5;
    const ember = scene.add.circle(ex, 8, 0.9, i % 2 ? LAVA_HI : LAVA, 0.9);
    c.add(ember);
    scene.tweens.add({ targets: ember, y: -16, x: ex + (Math.random() * 4 - 2), alpha: { from: 0.9, to: 0 }, repeat: -1, duration: 850 + i * 160, delay: i * 180 });
  }

  c.add([aura, cape, capeGlow1, capeGlow2,
    legL, legR, lavaLegL, lavaLegR, footL, footR,
    torso, torsoHi, coreVentGlow, coreVent, crack1, crack2, crack3, lavaDrip,
    pauldronL, pauldronR, pauldronCrackL, spikeL, spikeR,
    swordGuard, swordBlade, swordCore, swordTip, ...swordFlame,
    helm, hornL, hornR, helmCrack, visorGlow, visor]);
}

// ---------- SHADOW (fast wispy dark phantom, low-opacity, glowing eyes) ----------
function buildShadow(scene: Phaser.Scene, c: Phaser.GameObjects.Container): void {
  // VOID WRAITH — a fast phantom of living darkness. It leaves flickering
  // after-images as it darts forward, void tendrils writhe from its form, a
  // cluster of cold violet eyes glares from the dark, and a wisp-scythe trails behind.
  const DARK = 0x140a28;
  const DARK_HI = 0x3a1a6a;
  const EYE = 0x9a4aff;
  const EYE_HI = 0xc89aff;

  // ---- AFTER-IMAGE CLONES (motion echoes lagging behind) ----
  const echo1 = scene.add.polygon(-5, 3, [0, -10, -6, -3, -5, 6, 0, 10, 5, 6, 6, -3], DARK, 0.28);
  const echo2 = scene.add.polygon(-9, 4, [0, -9, -5, -2, -4, 5, 0, 8, 4, 5, 5, -2], DARK, 0.16);
  scene.tweens.add({ targets: echo1, alpha: { from: 0.3, to: 0.08 }, x: { from: -4, to: -8 }, yoyo: true, repeat: -1, duration: 360 });
  scene.tweens.add({ targets: echo2, alpha: { from: 0.18, to: 0.04 }, x: { from: -8, to: -13 }, yoyo: true, repeat: -1, duration: 420 });

  // ---- WISP-SCYTHE trailing (curved dark blade of smoke) ----
  const scythe = scene.add.arc(-7, -3, 7, 200, 320, false, EYE, 0.4).setStrokeStyle(1.4, EYE, 0.5);
  scene.tweens.add({ targets: scythe, alpha: { from: 0.4, to: 0.12 }, angle: { from: -10, to: 10 }, yoyo: true, repeat: -1, duration: 500 });

  // Smoke trail behind
  const trail1 = scene.add.ellipse(-6, 6, 8, 5, DARK, 0.4);
  const trail2 = scene.add.ellipse(-9, 8, 5, 3, DARK, 0.25);
  scene.tweens.add({ targets: [trail1, trail2], alpha: { from: 0.4, to: 0.1 }, scaleX: { from: 1, to: 1.4 }, yoyo: true, repeat: -1, duration: 400 });

  // ---- VOID TENDRILS writhing from the body ----
  const tendril1 = scene.add.line(0, 0, -5, 0, -9, -4, DARK_HI, 0.7).setLineWidth(1.2).setOrigin(0, 0);
  const tendril2 = scene.add.line(0, 0, 5, 0, 10, -3, DARK_HI, 0.7).setLineWidth(1.2).setOrigin(0, 0);
  const tendril3 = scene.add.line(0, 0, 4, 4, 9, 6, DARK_HI, 0.6).setLineWidth(1).setOrigin(0, 0);
  scene.tweens.add({ targets: [tendril1, tendril2, tendril3], angle: { from: -12, to: 12 }, alpha: { from: 0.7, to: 0.3 }, yoyo: true, repeat: -1, duration: 420, ease: "Sine.easeInOut" });

  // Wispy body — tapered ghost form
  const body = scene.add.polygon(0, 2, [0, -11, -7, -4, -6, 6, -3, 12, 0, 8, 3, 12, 6, 6, 7, -4], DARK, 0.8).setStrokeStyle(0.8, DARK_HI, 0.6);
  const bodyHi = scene.add.ellipse(0, -3, 8, 7, DARK_HI, 0.45);
  const bodyCore = scene.add.ellipse(0, -2, 4, 5, EYE, 0.15);
  scene.tweens.add({ targets: body, scaleY: { from: 1, to: 1.1 }, yoyo: true, repeat: -1, duration: 320, ease: "Sine.easeInOut" });
  // Tattered lower wisps
  const wispL = scene.add.triangle(-3, 10, 0, -4, -2, 5, 1, 4, DARK, 0.6);
  const wispM = scene.add.triangle(0, 12, 0, -5, -1.5, 5, 1.5, 5, DARK, 0.5);
  const wispR = scene.add.triangle(3, 10, 0, -4, 2, 5, -1, 4, DARK, 0.6);
  scene.tweens.add({ targets: [wispL, wispM, wispR], scaleY: { from: 1, to: 1.6 }, alpha: { from: 0.6, to: 0.15 }, yoyo: true, repeat: -1, duration: 300 });
  // Cluster of cold violet eyes (2 large + 2 small)
  const eyeL = scene.add.ellipse(-2.2, -5, 1.8, 1, EYE_HI, 1).setRotation(-0.3);
  const eyeR = scene.add.ellipse(2.2, -5, 1.8, 1, EYE_HI, 1).setRotation(0.3);
  const eyeSL = scene.add.circle(-3.6, -3, 0.7, EYE, 0.9);
  const eyeSR = scene.add.circle(3.6, -3, 0.7, EYE, 0.9);
  const eyeGlowL = scene.add.circle(-2.2, -5, 2.6, EYE, 0.3);
  const eyeGlowR = scene.add.circle(2.2, -5, 2.6, EYE, 0.3);
  scene.tweens.add({ targets: [eyeL, eyeR, eyeSL, eyeSR, eyeGlowL, eyeGlowR], alpha: { from: 0.45, to: 1 }, yoyo: true, repeat: -1, duration: 360 });

  c.add([echo2, echo1, scythe, trail1, trail2, tendril1, tendril2, tendril3,
    body, bodyHi, bodyCore, wispL, wispM, wispR,
    eyeGlowL, eyeGlowR, eyeL, eyeR, eyeSL, eyeSR]);
}
